import { CalendarDate, daysInMonth } from '../core/calendar-date'
import {
  createDateConstraint,
  createMonthConstraint,
  createYearConstraint,
  createRangeValidator,
  createDisabledReasons,
} from '../core/constraints'
import type {
  DateConstraintOptions,
  DateConstraintRule,
  ConstraintMessages,
} from '../core/constraints'
import { generateMonths, generateMonthGrid, generateYearGrid } from '../core/grid'
import type { DayCell, MonthGrid, MonthCell, YearCell } from '../core/grid'
import { SingleSelection, MultipleSelection, RangeSelection } from '../core/selection'
import type { Selection } from '../core/selection'
import { formatDate, formatRange, formatMultiple } from '../input/formatter'
import { parseDate, parseDateRange, parseDateMultiple } from '../input/parser'
import { attachMask } from '../input/mask'
import { formatHasMonthName } from '../input/month-names'
import type { RangePreset } from '../core/presets'
import { normalizeDateMeta } from '../core/metadata'
import type { DateMeta, DateMetaProvider } from '../core/metadata'
import { generateCalendarTemplate } from './template'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Mobile breakpoint media query. Must match the `@media (max-width: 639px)` rules in calendar.css. */
const MOBILE_BREAKPOINT = '(max-width: 639px)'

/**
 * Upper bound on how many years month-precision prev()/next() will scan past
 * disabled years before treating a direction as exhausted. Generously larger than
 * any realistic month picker span; it only bounds wasted work at a min/max boundary
 * where every year ahead is disabled (and so terminates the search).
 */
const MONTH_NAV_YEAR_SCAN = 120

/**
 * Upper bound on how many months days-view prev()/next() will scan past disabled
 * months before treating a direction as exhausted. Mirrors MONTH_NAV_YEAR_SCAN at
 * month granularity (120 years × 12 months); it only bounds wasted work at a
 * min/max boundary where every month ahead is disabled (and so terminates the
 * search). Generously larger than any realistic gap of fully-unavailable months.
 */
const MONTH_NAV_MONTH_SCAN = MONTH_NAV_YEAR_SCAN * 12

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Period-specific constraint rule (string-based for Alpine/HTML config). */
export interface CalendarConfigRule {
  /** Start of the period (ISO string). Required unless `months` is set. */
  from?: string
  /** End of the period (ISO string). Required unless `months` is set. */
  to?: string
  /** Recurring months this rule applies to (1=Jan, …, 12=Dec). Matches every year. */
  months?: number[]
  /** Priority for conflict resolution. Higher values win. Default: 0. */
  priority?: number
  /** Minimum selectable date within this period (ISO string). */
  minDate?: string
  /** Maximum selectable date within this period (ISO string). */
  maxDate?: string
  /** Specific dates to disable within this period (ISO strings). */
  disabledDates?: string[]
  /** Days of the week to disable within this period (0=Sun, 6=Sat). */
  disabledDaysOfWeek?: number[]
  /** Specific dates to force-enable within this period (ISO strings). */
  enabledDates?: string[]
  /** Days of the week to enable within this period (whitelist, 0=Sun, 6=Sat). */
  enabledDaysOfWeek?: number[]
  /** Specific months to disable within this period (1=Jan, 12=Dec). */
  disabledMonths?: number[]
  /** Months to enable within this period (whitelist, 1=Jan, 12=Dec). */
  enabledMonths?: number[]
  /** Specific years to disable within this period. */
  disabledYears?: number[]
  /** Years to enable within this period (whitelist). */
  enabledYears?: number[]
  /** Minimum range length in days (inclusive) for this period. */
  minRange?: number
  /** Maximum range length in days (inclusive) for this period. */
  maxRange?: number
}

export type { RangePreset } from '../core/presets'

export interface CalendarConfig {
  /** Selection mode. Default: 'single'. */
  mode?: 'single' | 'multiple' | 'range'
  /**
   * Selection precision. Default: 'day'.
   *
   * `'month'` turns the calendar into a forward-looking month picker: it opens on the
   * months grid, clicking a month commits the first day of that month as the selection
   * (emitting `calendar:change` with `dates[0]` = first-of-month), and the day grid is
   * never shown. The bound input becomes read-only (selection-only). Designed to compose
   * with `mode: 'single'`. The opening month follows the usual precedence
   * (`value` > `initialMonth` > today) and is clamped into `[minDate, maxDate]`.
   */
  precision?: 'day' | 'month'
  /** Display mode. Default: 'inline'. */
  display?: 'inline' | 'popup'
  /** Date format string (e.g. 'DD/MM/YYYY'). Default: 'DD/MM/YYYY'. */
  format?: string
  /** Number of months to show. 1 = single, 2 = side-by-side dual, 3+ = scrollable. Default: 1. */
  months?: number
  /** Number of months to show on mobile viewports (<640px). Only used when months === 2. Default: same as months. */
  mobileMonths?: number
  /** Max height of the scrollable container in px (only used when months >= 3). Default: 400. */
  scrollHeight?: number
  /** First day of the week (0=Sun, 1=Mon, …, 6=Sat). Default: 1. */
  firstDay?: number
  /** Minimum selectable date (ISO string). */
  minDate?: string
  /** Maximum selectable date (ISO string). */
  maxDate?: string
  /** Specific dates to disable (ISO strings). */
  disabledDates?: string[]
  /** Days of the week to disable (0=Sun, 6=Sat). */
  disabledDaysOfWeek?: number[]
  /** Specific dates to force-enable (ISO strings). Overrides disabled checks. */
  enabledDates?: string[]
  /** Days of the week to enable (whitelist — all other days disabled). */
  enabledDaysOfWeek?: number[]
  /** Specific months to disable (1=Jan, 12=Dec). */
  disabledMonths?: number[]
  /** Months to enable (whitelist — all other months disabled, 1=Jan, 12=Dec). */
  enabledMonths?: number[]
  /** Specific years to disable. */
  disabledYears?: number[]
  /** Years to enable (whitelist — all other years disabled). */
  enabledYears?: number[]
  /** Minimum range length in days (inclusive). Only used in range mode. */
  minRange?: number
  /** Maximum range length in days (inclusive). Only used in range mode. */
  maxRange?: number
  /** Period-specific constraint rules. Highest priority matching rule wins; ties broken by array order. */
  rules?: CalendarConfigRule[]
  /** Enable birth-date wizard mode. Default: false. Use 'year-month' or 'month-day' for half-wizard. */
  wizard?: boolean | 'year-month' | 'month-day'
  /** Enable input masking. Default: true. */
  mask?: boolean
  /** IANA timezone for "today" resolution. */
  timezone?: string
  /** BCP 47 locale string. */
  locale?: string
  /** Initial value (ISO string or formatted string). */
  value?: string
  /** Name attribute for hidden form input(s). */
  name?: string
  /** ID attribute for the popup input element. Allows external `<label for="...">` association. */
  inputId?: string
  /** Alpine x-ref name for the input element. Default: 'rc-input'. */
  inputRef?: string
  /** Show ISO 8601 week numbers on the left side of the day grid. Default: false. */
  showWeekNumbers?: boolean
  /**
   * Month to display when the calendar first opens, without affecting selection.
   * Accepts an ISO month (`'2027-04'`) or full date (`'2027-04-15'`); only year/month are used.
   * Precedence for initial view: explicit selection (`value`) > `initialMonth` > today.
   * Has no effect in wizard year-month / full mode (those center on `today.year - 30`).
   */
  initialMonth?: string
  /**
   * Predefined date range presets (e.g., "Today", "Last 7 Days", "This Month").
   * Displayed as quick-select buttons alongside the calendar. Only meaningful in range or single mode.
   */
  presets?: RangePreset[]
  /** Close popup after a date is selected. Default: true. */
  closeOnSelect?: boolean
  /**
   * Whether clicking an already-selected date deselects it. Default: true.
   *
   * When `false`, re-clicking the selected date in single mode (or an
   * already-selected date in multiple mode) keeps the selection: no toggle,
   * no `calendar:change`. In popup display the click still closes the popup,
   * exactly like a completed selection — it reads as "confirm this date".
   *
   * Only click/keyboard selection is affected; `clearSelection()`, `clear()`,
   * `setValue()`, and range mode behave the same regardless of this option.
   */
  allowDeselect?: boolean
  /**
   * Callback invoked before a date is selected. Return `false` to prevent the selection.
   *
   * Receives the date being selected and context about the current selection state.
   * Called after built-in constraint checks (disabled dates, range validation) pass.
   *
   * Usage:
   * ```js
   * calendar({
   *   beforeSelect(date, { mode, selectedDates, action }) {
   *     // Prevent selecting more than 5 dates
   *     if (mode === 'multiple' && action === 'select' && selectedDates.length >= 5) return false
   *     return true
   *   }
   * })
   * ```
   */
  beforeSelect?: (
    date: CalendarDate,
    context: {
      /** Current selection mode. */
      mode: 'single' | 'multiple' | 'range'
      /** Currently selected dates (before this action). */
      selectedDates: CalendarDate[]
      /** Whether this click would select or deselect the date. */
      action: 'select' | 'deselect'
    },
  ) => boolean | undefined
  /** Per-date metadata (labels, availability, colors). Static map or callback. */
  dateMetadata?: DateMetaProvider
  /** Custom messages for disabled-date tooltips. Overrides default English strings. */
  constraintMessages?: ConstraintMessages
  /** Auto-render template when no `.rc-calendar` exists in the container. Default: true. Set false to require manual template. */
  template?: boolean
}

// ---------------------------------------------------------------------------
// Alpine magic property accessor
// ---------------------------------------------------------------------------

/** Alpine injects these properties on the component proxy at runtime. */
interface AlpineMagics {
  $dispatch(event: string, detail?: Record<string, unknown>): void
  $watch(property: string, callback: (newValue: unknown) => void): void
  $refs: Record<string, HTMLElement>
  $nextTick(callback: () => void): void
  $el: HTMLElement
}

/**
 * Type-safe accessor for Alpine magic properties on `this`.
 * Alpine injects $dispatch, $watch, etc. onto the reactive proxy at runtime.
 */
function alpine(self: unknown): AlpineMagics {
  return self as AlpineMagics
}

// ---------------------------------------------------------------------------
// Config parsing helpers
// ---------------------------------------------------------------------------

function parseISODate(s: string): CalendarDate | null {
  return CalendarDate.fromISO(s)
}

function parseISODates(arr: string[]): CalendarDate[] {
  return arr.map((s) => CalendarDate.fromISO(s)).filter((d): d is CalendarDate => d !== null)
}

function parseConfigRule(rule: CalendarConfigRule): DateConstraintRule | null {
  const from = rule.from ? parseISODate(rule.from) : null
  const to = rule.to ? parseISODate(rule.to) : null

  // A rule must have a date range (full or half-open) or recurring months
  const hasDateRange = from !== null || to !== null
  const hasMonths = Array.isArray(rule.months) && rule.months.length > 0
  if (!hasDateRange && !hasMonths) return null

  const result: DateConstraintRule = {}
  if (from !== null) result.from = from
  if (to !== null) result.to = to
  if (hasMonths) {
    result.months = rule.months
  }
  if (rule.priority !== undefined) {
    result.priority = rule.priority
  }

  if (rule.minDate) {
    const d = parseISODate(rule.minDate)
    if (d) result.minDate = d
  }
  if (rule.maxDate) {
    const d = parseISODate(rule.maxDate)
    if (d) result.maxDate = d
  }
  if (rule.disabledDates) {
    result.disabledDates = parseISODates(rule.disabledDates)
  }
  if (rule.disabledDaysOfWeek) {
    result.disabledDaysOfWeek = rule.disabledDaysOfWeek
  }
  if (rule.enabledDates) {
    result.enabledDates = parseISODates(rule.enabledDates)
  }
  if (rule.enabledDaysOfWeek) {
    result.enabledDaysOfWeek = rule.enabledDaysOfWeek
  }
  if (rule.disabledMonths) {
    result.disabledMonths = rule.disabledMonths
  }
  if (rule.enabledMonths) {
    result.enabledMonths = rule.enabledMonths
  }
  if (rule.disabledYears) {
    result.disabledYears = rule.disabledYears
  }
  if (rule.enabledYears) {
    result.enabledYears = rule.enabledYears
  }
  if (rule.minRange !== undefined) {
    result.minRange = rule.minRange
  }
  if (rule.maxRange !== undefined) {
    result.maxRange = rule.maxRange
  }

  return result
}

// ---------------------------------------------------------------------------
// Config validation
// ---------------------------------------------------------------------------

const warn = (msg: string) => console.warn(`[reach-calendar] ${msg}`)

/**
 * Keys whose values are arrays at runtime but commonly stringified by templating
 * engines (Blade, Twig, etc.). When a JSON-string is supplied we tolerate it,
 * parse it, and warn loudly on malformed input. The TypeScript signature on
 * CalendarConfig stays as the array type — string input is a runtime convenience
 * for non-TS hosts.
 */
const COERCIBLE_ARRAY_KEYS = [
  'disabledDates',
  'disabledDaysOfWeek',
  'enabledDates',
  'enabledDaysOfWeek',
  'disabledMonths',
  'enabledMonths',
  'disabledYears',
  'enabledYears',
] as const

function coerceConfigArrays(config: CalendarConfig): void {
  const cfg = config as Record<string, unknown>
  for (const key of COERCIBLE_ARRAY_KEYS) {
    const value = cfg[key]
    if (value === undefined || value === null || Array.isArray(value)) continue
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value)
        if (Array.isArray(parsed)) {
          cfg[key] = parsed
          continue
        }
        warn(`${key}: JSON string did not parse to an array, got: ${value}`)
      } catch {
        warn(`${key}: invalid JSON string, got: ${value}`)
      }
      cfg[key] = undefined
    } else {
      warn(`${key}: expected array or JSON-array string, got ${typeof value}`)
      cfg[key] = undefined
    }
  }
}

/**
 * Validate calendar configuration and warn about invalid combinations.
 * Does not throw — the component still initialises with best-effort defaults.
 */
function validateConfig(config: CalendarConfig): void {
  // months must be a positive integer
  if (config.months !== undefined && (config.months < 1 || !Number.isInteger(config.months))) {
    warn(`months must be a positive integer, got: ${config.months}`)
  }

  // mobileMonths must be a positive integer
  if (config.mobileMonths !== undefined) {
    if (config.mobileMonths < 1 || !Number.isInteger(config.mobileMonths)) {
      warn(`mobileMonths must be a positive integer, got: ${config.mobileMonths}`)
    } else if (config.mobileMonths > 24) {
      warn(
        `mobileMonths (${config.mobileMonths}) is unusually large and will generate a very long scrollable grid`,
      )
    }
    if (config.wizard && config.mobileMonths >= 3) {
      warn('mobileMonths >= 3 is ignored in wizard mode; wizard cannot use a scrollable layout')
    }
  }

  // wizard + scrollable combo
  if (config.wizard && config.months && config.months >= 3) {
    warn('months >= 3 (scrollable) is not compatible with wizard mode; using months: 1')
  }

  // firstDay must be 0-6
  if (config.firstDay !== undefined && (config.firstDay < 0 || config.firstDay > 6)) {
    warn(`firstDay must be 0-6, got: ${config.firstDay}`)
  }

  // Invalid date strings
  if (config.minDate && !CalendarDate.fromISO(config.minDate)) {
    warn(`invalid minDate: "${config.minDate}"`)
  }
  if (config.maxDate && !CalendarDate.fromISO(config.maxDate)) {
    warn(`invalid maxDate: "${config.maxDate}"`)
  }

  // minDate must be before maxDate
  if (config.minDate && config.maxDate) {
    const min = CalendarDate.fromISO(config.minDate)
    const max = CalendarDate.fromISO(config.maxDate)
    if (min && max && min.isAfter(max)) {
      warn(`minDate "${config.minDate}" is after maxDate "${config.maxDate}"`)
    }
  }

  // minRange <= maxRange
  if (
    config.minRange !== undefined &&
    config.maxRange !== undefined &&
    config.minRange > config.maxRange
  ) {
    warn(`minRange (${config.minRange}) exceeds maxRange (${config.maxRange})`)
  }

  // wizard with non-single mode
  if (config.wizard && config.mode && config.mode !== 'single') {
    warn(
      `wizard mode is designed for single selection; mode "${config.mode}" may not work as expected`,
    )
  }

  // timezone must be a valid IANA timezone string
  if (config.timezone) {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: config.timezone })
    } catch {
      warn(`invalid timezone: "${config.timezone}"`)
    }
  }

  // initialMonth must parse as an ISO month or date
  if (config.initialMonth && !parseInitialMonth(config.initialMonth)) {
    warn(`invalid initialMonth: "${config.initialMonth}" (expected 'YYYY-MM' or 'YYYY-MM-DD')`)
  }

  // precision must be 'day' or 'month'
  if (
    config.precision !== undefined &&
    config.precision !== 'day' &&
    config.precision !== 'month'
  ) {
    warn(`invalid precision: "${config.precision}" (expected 'day' or 'month')`)
  }

  // precision: 'month' compatibility
  if (config.precision === 'month') {
    if (config.mode && config.mode !== 'single') {
      warn(
        `precision: 'month' is designed for single selection; mode "${config.mode}" may not work as expected`,
      )
    }
    if (config.wizard) {
      warn(`precision: 'month' overrides wizard mode; the wizard UI is ignored`)
    }
    if (config.format && /D/.test(config.format)) {
      warn(
        `precision: 'month' with a day-based format "${config.format}"; use a month-only format like 'MM/YYYY' or 'MMMM YYYY'`,
      )
    }
  }
}

/**
 * Parse an `initialMonth` config value into a CalendarDate anchored at day 1.
 * Accepts `'YYYY-MM'` or any ISO date string (`'YYYY-MM-DD'`); only year/month are used.
 * Returns null on invalid input — caller is responsible for warning + fallback.
 */
function parseInitialMonth(value: string): CalendarDate | null {
  const monthMatch = /^(\d{4})-(\d{2})$/.exec(value)
  if (monthMatch) {
    const year = Number(monthMatch[1])
    const month = Number(monthMatch[2])
    if (month < 1 || month > 12) return null
    return new CalendarDate(year, month, 1)
  }
  return CalendarDate.fromISO(value)?.startOfMonth() ?? null
}

// ---------------------------------------------------------------------------
// Constraint builder (reusable for initial config + runtime updates)
// ---------------------------------------------------------------------------

interface ConstraintFunctions {
  isDisabledDate: (d: CalendarDate) => boolean
  isRangeValid: (start: CalendarDate, end: CalendarDate) => boolean
  isMonthDisabled: (year: number, month: number) => boolean
  isYearDisabled: (year: number) => boolean
  getDisabledReasons: (d: CalendarDate) => string[]
}

/**
 * Build constraint functions from a CalendarConfig (or partial update).
 * Reused by both initial config parsing and `updateConstraints()`.
 */
function buildConstraints(
  cfg: Partial<CalendarConfig>,
  messages?: ConstraintMessages,
): ConstraintFunctions {
  const opts: DateConstraintOptions = {}

  if (cfg.minDate) {
    const d = CalendarDate.fromISO(cfg.minDate)
    if (d) opts.minDate = d
  }
  if (cfg.maxDate) {
    const d = CalendarDate.fromISO(cfg.maxDate)
    if (d) opts.maxDate = d
  }
  if (cfg.disabledDates) {
    opts.disabledDates = parseISODates(cfg.disabledDates)
  }
  if (cfg.disabledDaysOfWeek) {
    opts.disabledDaysOfWeek = cfg.disabledDaysOfWeek
  }
  if (cfg.enabledDates) {
    opts.enabledDates = parseISODates(cfg.enabledDates)
  }
  if (cfg.enabledDaysOfWeek) {
    opts.enabledDaysOfWeek = cfg.enabledDaysOfWeek
  }
  if (cfg.disabledMonths) {
    opts.disabledMonths = cfg.disabledMonths
  }
  if (cfg.enabledMonths) {
    opts.enabledMonths = cfg.enabledMonths
  }
  if (cfg.disabledYears) {
    opts.disabledYears = cfg.disabledYears
  }
  if (cfg.enabledYears) {
    opts.enabledYears = cfg.enabledYears
  }
  if (cfg.minRange !== undefined) {
    opts.minRange = cfg.minRange
  }
  if (cfg.maxRange !== undefined) {
    opts.maxRange = cfg.maxRange
  }
  if (cfg.rules) {
    const parsedRules = cfg.rules
      .map((r) => parseConfigRule(r))
      .filter((r): r is DateConstraintRule => r !== null)
    if (parsedRules.length > 0) {
      opts.rules = parsedRules
    }
  }

  return {
    isDisabledDate: createDateConstraint(opts),
    isRangeValid: createRangeValidator(opts),
    isMonthDisabled: createMonthConstraint(opts),
    isYearDisabled: createYearConstraint(opts),
    getDisabledReasons: createDisabledReasons(opts, messages),
  }
}

/** Constraint-related keys from CalendarConfig. */
const CONSTRAINT_KEYS = [
  'minDate',
  'maxDate',
  'disabledDates',
  'disabledDaysOfWeek',
  'enabledDates',
  'enabledDaysOfWeek',
  'disabledMonths',
  'enabledMonths',
  'disabledYears',
  'enabledYears',
  'minRange',
  'maxRange',
  'rules',
] as const

type ConstraintConfig = Pick<CalendarConfig, (typeof CONSTRAINT_KEYS)[number]>

/** Extract only the constraint-related properties from a config object. */
function extractConstraintConfig(cfg: Partial<CalendarConfig>): ConstraintConfig {
  const result: Partial<ConstraintConfig> = {}
  for (const key of CONSTRAINT_KEYS) {
    if (cfg[key] !== undefined) {
      ;(result as Record<string, unknown>)[key] = cfg[key]
    }
  }
  return result as ConstraintConfig
}

// ---------------------------------------------------------------------------
// Component factory
// ---------------------------------------------------------------------------

/**
 * Create the Alpine component data object for the calendar.
 *
 * This factory is called by `Alpine.data('calendar', createCalendarData)`.
 * The returned object becomes the reactive component state.
 */
export function createCalendarData(
  config: CalendarConfig = {},
  Alpine?: { initTree: (el: HTMLElement) => void },
) {
  // --- Normalize JSON-string array inputs (Blade/Twig interop) before validation ---
  coerceConfigArrays(config)

  // --- Validate config ---
  validateConfig(config)

  // --- Parse config with defaults ---
  const mode = config.mode ?? 'single'
  const precision = config.precision ?? 'day'
  const display = config.display ?? 'inline'
  // Month-precision pickers default to a month-only display format.
  const format = config.format ?? (precision === 'month' ? 'MM/YYYY' : 'DD/MM/YYYY')
  const firstDay = config.firstDay ?? 1
  let timezone = config.timezone
  if (timezone) {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: timezone })
    } catch {
      timezone = undefined
    }
  }
  // precision: 'month' takes precedence over the wizard (validateConfig warns this).
  // Neutralize the wizard at the source so every downstream derivation — wizard chrome
  // in the template, the initial view, and the open() reopen path — ignores it
  // consistently, rather than only some of them honoring the override.
  const wizardConfig = precision === 'month' ? false : (config.wizard ?? false)
  const rawMonthCount = config.months ?? 1
  // Force months: 1 when wizard + scrollable
  const desktopMonthCount = wizardConfig && rawMonthCount >= 3 ? 1 : rawMonthCount

  // mobileMonths: applies for any desktop count; mobile count can be smaller or larger.
  // Wizard mode only conflicts with the scrollable (>=3) layout, not with a 1- or 2-month
  // mobile count, so honor mobileMonths for non-scrollable wizard configs and fall back
  // to desktopMonthCount only when the value would push mobile into scroll mode.
  const rawMobileMonths = config.mobileMonths
  const hasMobileMonths = rawMobileMonths !== undefined && rawMobileMonths !== desktopMonthCount
  let mobileMonthCount: number
  // Fall back to desktop count for NaN / Infinity / -Infinity — validateConfig warns,
  // but a bad value still reaches here through the `unknown ?? default` path. Without
  // the Number.isFinite guard, NaN would make both needsDayView and needsScrollableView
  // false (leaving no template branch rendered), and Infinity would hang _rebuildGrid.
  if (!hasMobileMonths || !Number.isFinite(rawMobileMonths)) {
    mobileMonthCount = desktopMonthCount
  } else {
    const normalized = Math.max(1, Math.floor(rawMobileMonths as number))
    // Wizard + scrollable is incompatible (validateConfig warns above); drop the
    // mobile value rather than render a broken wizard at the mobile breakpoint.
    mobileMonthCount = wizardConfig && normalized >= 3 ? desktopMonthCount : normalized
  }

  // Which template branches are needed across breakpoints?
  const needsDayView = Math.min(mobileMonthCount, desktopMonthCount) <= 2
  const needsScrollableView = Math.max(mobileMonthCount, desktopMonthCount) >= 3
  const isDualChrome = needsDayView && (mobileMonthCount === 2 || desktopMonthCount === 2)

  // Reuse a single MediaQueryList for both initial detection and listener setup
  const mobileMql =
    hasMobileMonths &&
    mobileMonthCount !== desktopMonthCount &&
    typeof window !== 'undefined' &&
    window.matchMedia
      ? window.matchMedia(MOBILE_BREAKPOINT)
      : null
  const monthCount = mobileMql?.matches ? mobileMonthCount : desktopMonthCount
  const isScrollable = monthCount >= 3
  const scrollHeight = config.scrollHeight ?? 400
  const wizard = !!wizardConfig
  const wizardMode: 'none' | 'full' | 'year-month' | 'month-day' =
    wizardConfig === true ? 'full' : wizardConfig === false ? 'none' : wizardConfig
  const wizardTotalSteps = wizardMode === 'none' ? 0 : wizardMode === 'full' ? 3 : 2
  const wizardStartView: 'days' | 'months' | 'years' =
    wizardMode === 'full' || wizardMode === 'year-month'
      ? 'years'
      : wizardMode === 'month-day'
        ? 'months'
        : 'days'
  const useMask = config.mask ?? true
  const showWeekNumbers = config.showWeekNumbers ?? false
  const presets: RangePreset[] = config.presets ?? []
  const inputName = config.name ?? ''
  const inputId = config.inputId ?? null
  const inputRef = config.inputRef ?? 'rc-input'
  const locale = config.locale
  const closeOnSelect = config.closeOnSelect ?? mode !== 'multiple'
  const allowDeselect = config.allowDeselect ?? true
  const beforeSelectCb = config.beforeSelect ?? null

  // --- Build constraint functions ---
  const constraintMessages = config.constraintMessages
  const constraints = buildConstraints(config, constraintMessages)

  // --- Normalize date metadata provider ---
  const initialGetDateMeta = normalizeDateMeta(config.dateMetadata)

  // --- Create selection model ---
  function buildSelection(): Selection {
    if (mode === 'multiple') return new MultipleSelection()
    if (mode === 'range') return new RangeSelection()
    return new SingleSelection()
  }

  const selection = buildSelection()

  // --- Parse initial value ---
  const isInitDisabled = (d: CalendarDate) =>
    constraints.isDisabledDate(d) || initialGetDateMeta(d)?.availability === 'unavailable'

  // Month-level disabled check for precision: 'month' restore. Mirrors the runtime
  // _isMonthDisabled (the deep check in _wrapWithDeepChecks): a month is disabled when
  // the shallow month constraint rejects it OR every day in it is effectively disabled.
  const isInitMonthDisabled = (year: number, month: number): boolean => {
    if (constraints.isMonthDisabled(year, month)) return true
    const count = daysInMonth(year, month)
    for (let day = 1; day <= count; day++) {
      if (!isInitDisabled(new CalendarDate(year, month, day))) return false
    }
    return true
  }

  if (config.value) {
    if (mode === 'single') {
      const d = parseDate(config.value, format, locale) ?? CalendarDate.fromISO(config.value)
      if (d) {
        if (precision === 'month') {
          // The month is the unit of selection: accept the value when its month is
          // selectable and normalize to the first of the month — matching the value
          // _commitMonth() emits. The day-level isInitDisabled check would wrongly drop
          // a first-of-month value that precedes a mid-month minDate (e.g. value
          // 2026-06-01 with minDate 2026-06-15), even though the picker allows June.
          if (!isInitMonthDisabled(d.year, d.month)) {
            selection.toggle(new CalendarDate(d.year, d.month, 1))
          }
        } else if (!isInitDisabled(d)) {
          selection.toggle(d)
        }
      }
    } else if (mode === 'range') {
      const range = parseDateRange(config.value, format, locale)
      if (range) {
        let [start, end] = range
        if (end.isBefore(start)) {
          const tmp = start
          start = end
          end = tmp
        }
        if (
          !isInitDisabled(start) &&
          !isInitDisabled(end) &&
          constraints.isRangeValid(start, end)
        ) {
          ;(selection as RangeSelection).setRange(start, end)
        }
      }
    } else if (mode === 'multiple') {
      const dates = parseDateMultiple(config.value, format, locale)
      for (const d of dates) {
        if (!isInitDisabled(d)) (selection as MultipleSelection).add(d)
      }
    }
  }

  // --- Resolve today ---
  const today = CalendarDate.today(timezone)

  // --- Determine initial viewing month/year ---
  // Precedence: explicit selection > initialMonth hint > today.
  const initialDates = selection.toArray()
  const initialMonthDate = config.initialMonth ? parseInitialMonth(config.initialMonth) : null
  const defaultViewDate = (
    initialDates.length > 0 ? initialDates[0] : (initialMonthDate ?? today)
  ) as CalendarDate

  // For precision: 'month', clamp the opening view into [minDate, maxDate] so the picker
  // never opens on a fully out-of-range year (e.g. when today precedes minDate).
  const minViewBound = config.minDate ? CalendarDate.fromISO(config.minDate) : null
  const maxViewBound = config.maxDate ? CalendarDate.fromISO(config.maxDate) : null
  const clampViewToRange = (d: CalendarDate): CalendarDate => {
    if (minViewBound && d.isBefore(minViewBound.startOfMonth())) {
      return new CalendarDate(minViewBound.year, minViewBound.month, 1)
    }
    if (maxViewBound && d.isAfter(maxViewBound.endOfMonth())) {
      return new CalendarDate(maxViewBound.year, maxViewBound.month, 1)
    }
    return d
  }

  // Initial view position:
  // - precision month: forward-looking; honors value > initialMonth > today, clamped to range.
  // - wizard full/year-month: center year picker around ~30 years ago (birth-date default).
  // - otherwise: the resolved defaultViewDate.
  const viewDate =
    precision === 'month'
      ? clampViewToRange(defaultViewDate)
      : wizardMode === 'full' || wizardMode === 'year-month'
        ? new CalendarDate(today.year - 30, today.month, today.day)
        : defaultViewDate

  // --- Compute initial inputValue ---
  function computeFormattedValue(sel: Selection): string {
    const dates = sel.toArray()
    if (dates.length === 0) return ''
    const first = dates[0] as CalendarDate
    if (mode === 'range' && dates.length === 2) {
      return formatRange(first, dates[1] as CalendarDate, format, locale)
    }
    if (mode === 'multiple') {
      return formatMultiple(dates, format, undefined, locale)
    }
    return formatDate(first, format, locale)
  }

  // ---------------------------------------------------------------------------
  // Return the Alpine data object
  // ---------------------------------------------------------------------------

  return {
    // --- Config (exposed to templates) ---
    mode,
    display,
    format,
    monthCount,
    firstDay,
    wizard,
    wizardMode,
    wizardTotalSteps,
    showWeekNumbers,
    presets,
    inputName,

    // --- Mobile months ---
    _mediaQueryCleanup: null as (() => void) | null,

    // --- Reactive state ---
    month: viewDate.month,
    year: viewDate.year,
    view: (precision === 'month' ? 'months' : wizard ? wizardStartView : 'days') as
      | 'days'
      | 'months'
      | 'years',
    isOpen: display === 'inline',
    grid: [] as MonthGrid[],
    monthGrid: [] as MonthCell[][],
    yearGrid: [] as YearCell[][],
    inputValue: computeFormattedValue(selection),
    popupStyle: display === 'popup' ? 'position:fixed;inset:0;z-index:50;' : '',
    focusedDate: null as CalendarDate | null,
    focusedMonth: null as number | null,
    hoverDate: null as CalendarDate | null,
    wizardStep: (wizard ? 1 : 0) as number,
    _wizardYear: null as number | null,
    _wizardMonth: null as number | null,
    _wizardDay: null as number | null,

    // --- Internal state ---
    _navDirection: '' as '' | 'next' | 'prev',
    _selection: selection,
    _today: today,
    _constraintConfig: extractConstraintConfig(config),
    _isDisabledDate: constraints.isDisabledDate,
    _isRangeValid: constraints.isRangeValid,
    _isMonthDisabled: constraints.isMonthDisabled as (year: number, month: number) => boolean,
    _isYearDisabled: constraints.isYearDisabled as (year: number) => boolean,
    _getDisabledReasons: constraints.getDisabledReasons,
    _inputEl: null as HTMLInputElement | null,
    _detachInput: null as (() => void) | null,
    _syncing: false,
    _suppressFocusOpen: false,
    _Alpine: (Alpine ?? null) as { initTree: (el: HTMLElement) => void } | null,
    _autoRendered: false,
    _popupOverlayEl: null as HTMLElement | null,
    _rootEl: null as HTMLElement | null,

    // Scrollable multi-month state
    isScrollable,
    _scrollHeight: scrollHeight,
    _scrollContainerEl: null as HTMLElement | null,
    _scrollObserver: null as IntersectionObserver | null,
    /**
     * Monotonic generation counter for _initScrollListener. Bumped on every fresh
     * bind entry so queued retries from an earlier session can detect that they're
     * stale and exit without double-binding an observer.
     */
    _scrollInitToken: 0,
    /** Index into grid[] of the month currently visible at top of scroll viewport. */
    _scrollVisibleIndex: 0,
    /**
     * Reactive counter bumped on selection changes. Read by dayClasses() so Alpine
     * re-evaluates class bindings without needing a full grid rebuild.
     */
    _selectionRev: 0,

    // --- Date metadata ---
    _getDateMeta: initialGetDateMeta,
    _metadataRev: 0,
    _dateMetaCache: null as Map<string, DateMeta | undefined> | null,

    _getCachedDateMeta(d: CalendarDate): DateMeta | undefined {
      if (!this._dateMetaCache) this._dateMetaCache = new Map()
      const key = d.toKey()
      if (this._dateMetaCache.has(key)) return this._dateMetaCache.get(key)
      const meta = this._getDateMeta(d)
      this._dateMetaCache.set(key, meta)
      return meta
    },

    /** Check if a date is effectively disabled (constraint OR metadata unavailability). */
    _isEffectivelyDisabled(d: CalendarDate): boolean {
      if (this._isDisabledDate(d)) return true
      return this._getDateMeta(d)?.availability === 'unavailable'
    },

    /** Return the first non-disabled day in a month, or null if all days are disabled. */
    _firstSelectableDay(year: number, month: number): CalendarDate | null {
      const count = daysInMonth(year, month)
      for (let d = 1; d <= count; d++) {
        const candidate = new CalendarDate(year, month, d)
        if (!this._isEffectivelyDisabled(candidate)) return candidate
      }
      return null
    },

    /**
     * Wrap shallow month/year constraint checks with deep day-level checks.
     * A month is disabled if the shallow check says so OR every day in it is effectively disabled.
     * A year is disabled if every month in it is disabled.
     */
    _monthDisabledCache: null as Map<string, boolean> | null,
    _yearDisabledCache: null as Map<number, boolean> | null,

    _wrapWithDeepChecks(c: ConstraintFunctions): void {
      const shallowMonth = c.isMonthDisabled
      this._isMonthDisabled = (year: number, month: number) => {
        if (!this._monthDisabledCache) this._monthDisabledCache = new Map()
        const key = `${year}-${month}`
        if (this._monthDisabledCache.has(key)) return this._monthDisabledCache.get(key) as boolean
        const result = shallowMonth(year, month) || this._firstSelectableDay(year, month) === null
        this._monthDisabledCache.set(key, result)
        return result
      }

      const shallowYear = c.isYearDisabled
      this._isYearDisabled = (year: number) => {
        if (!this._yearDisabledCache) this._yearDisabledCache = new Map()
        if (this._yearDisabledCache.has(year)) return this._yearDisabledCache.get(year) as boolean
        let result = false
        if (shallowYear(year)) {
          result = true
        } else {
          result = true
          for (let m = 1; m <= 12; m++) {
            if (!this._isMonthDisabled(year, m)) {
              result = false
              break
            }
          }
        }
        this._yearDisabledCache.set(year, result)
        return result
      }
    },

    // --- Accessibility: live region ---
    _statusMessage: '' as string,

    // --- Getters ---

    get selectedDates(): CalendarDate[] {
      return this._selection.toArray()
    },

    get formattedValue(): string {
      return computeFormattedValue(this._selection)
    },

    /** ISO string values for hidden form inputs. */
    get hiddenInputValues(): string[] {
      return this._selection.toArray().map((d: CalendarDate) => d.toISO())
    },

    /** ISO string of focused date for aria-activedescendant binding. */
    get focusedDateISO(): string {
      return this.focusedDate ? this.focusedDate.toISO() : ''
    },

    /**
     * The id the calendar's `aria-activedescendant` should point at. Tracks the
     * months grid while it is the active view (precision month, or the wizard's
     * month step) and the day grid otherwise, so the focused cell is announced in
     * every view that has keyboard navigation.
     */
    get activeDescendantId(): string | null {
      if (this.view === 'months') {
        return this.focusedMonth !== null ? `month-${this.focusedMonth}` : null
      }
      return this.focusedDateISO ? `day-${this.focusedDateISO}` : null
    },

    /** ID for the popup input element (for external label association). */
    get inputId(): string | null {
      return inputId
    },

    /** Accessible label for the popup input element. */
    get inputAriaLabel(): string {
      if (mode === 'range') return 'Select date range'
      if (mode === 'multiple') return 'Select dates'
      if (wizard) return 'Select birth date'
      return 'Select date'
    },

    /** Accessible label for the popup dialog. */
    get popupAriaLabel(): string {
      if (wizard) return 'Birth date wizard'
      if (mode === 'range') return 'Date range picker'
      return 'Date picker'
    },

    /** Label for the current wizard step (e.g. "Select Year"). */
    get wizardStepLabel(): string {
      if (!this.wizard) return ''
      if (wizardMode === 'full') {
        if (this.wizardStep === 1) return 'Select Year'
        if (this.wizardStep === 2) return 'Select Month'
        return 'Select Day'
      }
      if (wizardMode === 'year-month') {
        if (this.wizardStep === 1) return 'Select Year'
        return 'Select Month'
      }
      if (wizardMode === 'month-day') {
        if (this.wizardStep === 1) return 'Select Month'
        return 'Select Day'
      }
      return ''
    },

    /** Summary of wizard selections so far (e.g. "1995 · June · 15"). */
    get wizardSummary(): string {
      if (!wizard) return ''
      const parts: string[] = []
      if (this._wizardYear !== null) parts.push(String(this._wizardYear))
      if (this._wizardMonth !== null) {
        const d = new CalendarDate(this._wizardYear ?? this.year, this._wizardMonth, 1)
        parts.push(d.format({ month: 'long' }, locale))
      }
      if (this._wizardDay !== null) parts.push(String(this._wizardDay))
      return parts.join(' \u00b7 ')
    },

    /**
     * Localized short weekday headers in the correct order for the current `firstDay`.
     * Returns an array of 7 short names (e.g., ["Mo", "Tu", "We", ...]).
     */
    _weekdayHeaders: null as string[] | null,

    get weekdayHeaders(): string[] {
      if (this._weekdayHeaders) return this._weekdayHeaders
      const headers: string[] = []
      // Use a reference Sunday (Jan 4 2026 is a Sunday)
      const refSunday = new Date(2026, 0, 4)
      const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' })
      for (let i = 0; i < 7; i++) {
        const dayIndex = (this.firstDay + i) % 7
        const d = new Date(refSunday)
        d.setDate(refSunday.getDate() + dayIndex)
        headers.push(fmt.format(d))
      }
      this._weekdayHeaders = headers
      return headers
    },

    // --- Lifecycle ---

    init() {
      // Cache root element for dispatching events (avoids teleported popup context issues)
      this._rootEl = alpine(this).$el

      // Wrap shallow month/year checks with deep day-level constraint checks
      this._wrapWithDeepChecks(constraints)

      // Auto-template injection: append calendar template if not already present
      const el = this._rootEl
      const hasCalendar = el.querySelector('.rc-calendar') !== null
      if (!hasCalendar && config.template !== false && this._Alpine?.initTree) {
        // Extract consumer-supplied header/footer markup from
        // `<template data-rc-slot="header|footer">` siblings inside the x-data
        // root. Captured templates are removed from the DOM so they don't sit
        // around as orphans (the auto-rendered template will hold the markup).
        let headerSlot: string | undefined
        let footerSlot: string | undefined
        const slotTemplates = el.querySelectorAll(
          ':scope > template[data-rc-slot]',
        ) as NodeListOf<HTMLTemplateElement>
        slotTemplates.forEach((tpl) => {
          const name = tpl.getAttribute('data-rc-slot')
          if (name !== 'header' && name !== 'footer') {
            warn(`unknown data-rc-slot="${name ?? ''}"; expected "header" or "footer"`)
            return
          }
          const html = tpl.innerHTML.trim()
          if (html) {
            if (name === 'header' && headerSlot === undefined) headerSlot = html
            else if (name === 'footer' && footerSlot === undefined) footerSlot = html
          }
          tpl.remove()
        })

        const fragment = document.createRange().createContextualFragment(
          generateCalendarTemplate({
            display: this.display,
            needsDayView,
            needsScrollableView,
            isDualChrome,
            isWizard: this.wizardMode !== 'none',
            precisionMonth: precision === 'month',
            hasName: !!this.inputName,
            showWeekNumbers: this.showWeekNumbers,
            hasPresets: this.presets.length > 0,
            scrollHeight: this._scrollHeight,
            headerSlot,
            footerSlot,
          }),
        )
        const newElements = Array.from(fragment.children).filter(
          (c): c is HTMLElement => c instanceof HTMLElement,
        )
        el.appendChild(fragment)
        for (const child of newElements) {
          this._Alpine.initTree(child)
        }
        this._autoRendered = true

        // Teleport popup overlay to body to escape CSS containing block issues.
        // Preserve the Alpine scope chain so that x-if / x-for directives
        // evaluated after teleportation can still resolve component data.
        if (display === 'popup') {
          const overlay = el.querySelector('.rc-popup-overlay') as HTMLElement | null
          if (overlay) {
            const xEl = el as HTMLElement & { _x_dataStack?: unknown[] }
            const xOverlay = overlay as HTMLElement & { _x_dataStack?: unknown[] }
            if (xEl._x_dataStack) xOverlay._x_dataStack = xEl._x_dataStack
            document.body.appendChild(overlay)
            this._popupOverlayEl = overlay
            // Forward data-rc-theme so consumer-scoped CSS still applies inside
            // the portal (the overlay is no longer a descendant of the x-data root).
            this._syncPortalTheme()
          }
        }
      }

      this._rebuildGrid()
      this._rebuildMonthGrid()
      this._rebuildYearGrid()

      // Alpine $watch: rebuild grids and dispatch events on state changes
      alpine(this).$watch('month', () => {
        this._rebuildGrid()
        this._emitNavigate()
        this._announceNavigation()
        if (this.isScrollable) {
          alpine(this).$nextTick(() => {
            this._rebindScrollObserver()
          })
        }
      })
      alpine(this).$watch('year', () => {
        this._rebuildGrid()
        this._rebuildMonthGrid()
        this._rebuildYearGrid()
        this._emitNavigate()
        this._announceNavigation()
        if (this.isScrollable) {
          alpine(this).$nextTick(() => {
            this._rebindScrollObserver()
          })
        }
      })
      alpine(this).$watch('view', (value: unknown) => {
        this._emitViewChange()
        this._announceViewChange()
        // Re-entering the day view in scrollable mode: the scroll container may have
        // just been (re)mounted by x-if, so any existing observer's element references
        // are stale, or — if the breakpoint flipped while view was 'months'/'years' —
        // no observer was ever bound. _initScrollListener retries internally if the
        // DOM isn't ready, so a single tick is sufficient here.
        if (value === 'days' && this.isScrollable) {
          alpine(this).$nextTick(() => {
            this._rebindScrollObserver()
          })
        }
      })

      // Auto-bind to x-ref input if present
      alpine(this).$nextTick(() => {
        const refs = alpine(this).$refs
        if (refs && refs[inputRef] && refs[inputRef] instanceof HTMLInputElement) {
          this.bindInput(refs[inputRef])
        } else if (this.display === 'popup') {
          console.warn(
            `[reach-calendar] Popup mode requires an <input x-ref="${inputRef}"> inside the component container.`,
          )
        }
        // Init scroll listener for scrollable multi-month. Single $nextTick is enough
        // here: Alpine.initTree has already rendered the x-for by the time this runs,
        // so we only need to wait one reactive pass for the DOM to settle.
        if (this.isScrollable) {
          alpine(this).$nextTick(() => {
            this._initScrollListener()
          })
        }
      })

      // Set up responsive monthCount listener when mobileMonths differs from desktop.
      // Supports crossing the 3-month scrollable threshold in either direction by
      // flipping the reactive isScrollable flag; Alpine's x-if swaps the active view.
      if (mobileMql) {
        const handler = (e: MediaQueryListEvent) => {
          const newCount = e.matches ? mobileMonthCount : desktopMonthCount
          if (newCount === this.monthCount) return
          const wasScrollable = this.isScrollable
          const willBeScrollable = newCount >= 3
          // When leaving scrollable mode — or rebuilding while still scrollable with a
          // different count — re-anchor to the month the user was actually viewing
          // (tracked by the scroll observer). this.year/this.month holds the grid's top
          // anchor, not the scroll position, so without this the rebuild jumps back to
          // the original start month.
          //
          // Gate on view === 'days' because _scrollVisibleIndex is maintained only by
          // the day-grid scroll observer. If the user is currently in the month or year
          // picker, the index is stale relative to their picker context, and applying
          // it would silently shift this.year/this.month mid-selection.
          if (wasScrollable && this.view === 'days') {
            const visible = this.grid[this._scrollVisibleIndex]
            if (visible) {
              this.year = visible.year
              this.month = visible.month
            }
            // After re-anchor, the previously-visible month sits at grid[0]. Reset
            // the index synchronously so scrollHeaderLabel reads the correct entry
            // (otherwise it reads grid[oldIndex], which is out of bounds when the
            // new grid is shorter). Container scrollTop is reset below in the same
            // $nextTick as the observer rebind.
            this._scrollVisibleIndex = 0
          }
          this.monthCount = newCount
          this.isScrollable = willBeScrollable
          this._rebuildGrid()
          // If the user was keyboard-navigating, focusedDate may now point to a month
          // that's no longer rendered (aria-activedescendant would reference a missing
          // `day-<iso>` id). Clear it so the binding drops cleanly; the next navigation
          // keypress re-inits focusedDate via the existing auto-init path.
          if (this.focusedDate) {
            const fd = this.focusedDate
            const inGrid = this.grid.some((g) => g.year === fd.year && g.month === fd.month)
            if (!inGrid) {
              this.focusedDate = null
            }
          }
          if (this.isScrollable) {
            // Scrollable (newly crossed in OR staying scrollable with a different count).
            // _initScrollListener retries internally until the x-if/x-for chain renders.
            const didReanchor = wasScrollable && this.view === 'days'
            alpine(this).$nextTick(() => {
              // Scroll the container back to the top so the re-anchored grid[0]
              // is actually visible. Without this, scrollTop retains its old pixel
              // offset from the previous (longer) grid and the viewport lands on
              // the wrong month despite the correct index.
              if (didReanchor && this._scrollContainerEl) {
                this._scrollContainerEl.scrollTop = 0
              }
              this._rebindScrollObserver()
            })
          } else if (wasScrollable) {
            // Crossed out of scrollable — tear down the observer
            if (this._scrollObserver) {
              this._scrollObserver.disconnect()
              this._scrollObserver = null
            }
            this._scrollContainerEl = null
            this._scrollVisibleIndex = 0
          }
        }
        mobileMql.addEventListener('change', handler)
        this._mediaQueryCleanup = () => {
          mobileMql.removeEventListener('change', handler)
        }
      }
    },

    destroy() {
      if (this._mediaQueryCleanup) {
        this._mediaQueryCleanup()
        this._mediaQueryCleanup = null
      }
      if (this._scrollObserver) {
        this._scrollObserver.disconnect()
        this._scrollObserver = null
      }
      this._scrollContainerEl = null
      if (this._detachInput) {
        this._detachInput()
        this._detachInput = null
      }
      this._inputEl = null
      // Remove teleported popup from body
      if (this._popupOverlayEl) {
        this._popupOverlayEl.remove()
        this._popupOverlayEl = null
      }
      if (this._autoRendered) {
        const el = alpine(this).$el
        el.querySelector('.rc-popup-overlay')?.remove() // fallback for non-teleported
        el.querySelector('.rc-calendar')?.remove()
        this._autoRendered = false
      }
      this._rootEl = null
    },

    // --- Grid ---

    _rebuildGrid() {
      this._dateMetaCache = null
      this._monthDisabledCache = null
      this._yearDisabledCache = null
      this.grid = generateMonths(
        this.year,
        this.month,
        this.monthCount,
        this.firstDay,
        this._today,
        (d: CalendarDate) => this._isEffectivelyDisabled(d),
      )
    },

    /** Rebuild the 3×4 month picker grid for the current year. */
    _rebuildMonthGrid() {
      this.monthGrid = generateMonthGrid(this.year, this._today, locale, this._isMonthDisabled)
    },

    /** Rebuild the 3×4 year picker grid for the current year's 12-year block. */
    _rebuildYearGrid() {
      this.yearGrid = generateYearGrid(this.year, this._today, this._isYearDisabled)
    },

    /**
     * Build a flat array of grid items for template rendering, interleaving week number
     * markers with day cells. Used by the auto-rendered template when `showWeekNumbers` is true.
     */
    dayGridItems(
      mg: MonthGrid,
    ): { isWeekNumber: boolean; weekNumber: number; cell: DayCell; key: string }[] {
      const items: { isWeekNumber: boolean; weekNumber: number; cell: DayCell; key: string }[] = []
      for (let ri = 0; ri < mg.rows.length; ri++) {
        const row = mg.rows[ri]
        if (!row || row.length === 0) continue
        const firstCell = row[0] as DayCell
        items.push({
          isWeekNumber: true,
          weekNumber: mg.weekNumbers[ri] ?? 0,
          // Placeholder cell — template guards prevent access when isWeekNumber is true
          cell: firstCell,
          key: `wn-${ri}`,
        })
        for (const cell of row) {
          items.push({
            isWeekNumber: false,
            weekNumber: 0,
            cell,
            key: cell.date.toISO(),
          })
        }
      }
      return items
    },

    /** Year label for the month view header (e.g. "2026"). */
    get yearLabel(): string {
      return String(this.year)
    },

    /** Decade range label for the year view header (e.g. "2016 – 2027"). */
    get decadeLabel(): string {
      const startYear = Math.floor(this.year / 12) * 12
      return `${startYear} – ${startYear + 11}`
    },

    /**
     * Whether backward navigation is possible from the current position.
     *
     * - Days view: checks if any earlier month has selectable dates, hopping over a
     *   gap of fully-unavailable in-range months (stops only at minDate).
     * - Months view: checks if the previous year has any selectable months.
     * - Years view: checks if the previous 12-year block has any selectable years.
     *
     * Usage in Alpine template:
     * ```html
     * <button @click="prev()" :disabled="!canGoPrev">←</button>
     * ```
     */
    get canGoPrev(): boolean {
      if (this.view === 'days') {
        if (this.isScrollable) return false
        // Like the month-precision year jump, step over any run of fully-unavailable
        // months: navigation stays live whenever some earlier selectable month is
        // reachable, not only when the immediately-previous one happens to be. Months
        // outside [minDate, maxDate] are shallow-disabled, so the range still bounds it.
        return this._nearestSelectableMonth(-1) !== null
      }
      if (this.view === 'months') {
        // Month-precision pickers have no year grid to jump through, so prev()/next()
        // skip disabled years — enable the arrow whenever any earlier year is reachable,
        // not just when the immediately-previous one happens to be enabled.
        if (precision === 'month') return this._nearestSelectableYear(-1) !== null
        return !this._isYearDisabled(this.year - 1)
      }
      if (this.view === 'years') {
        const blockStart = Math.floor(this.year / 12) * 12
        // Check if any year in the previous 12-year block is enabled
        for (let y = blockStart - 12; y < blockStart; y++) {
          if (!this._isYearDisabled(y)) return true
        }
        return false
      }
      return true
    },

    /**
     * Whether forward navigation is possible from the current position.
     *
     * - Days view: checks if any later month has selectable dates, hopping over a
     *   gap of fully-unavailable in-range months (stops only at maxDate).
     * - Months view: checks if the next year has any selectable months.
     * - Years view: checks if the next 12-year block has any selectable years.
     *
     * Usage in Alpine template:
     * ```html
     * <button @click="next()" :disabled="!canGoNext">→</button>
     * ```
     */
    get canGoNext(): boolean {
      if (this.view === 'days') {
        if (this.isScrollable) return false
        // See canGoPrev: forward navigation hops over a gap of fully-unavailable
        // in-range months, so it stays live as long as a later selectable month is
        // reachable. Out-of-range months are shallow-disabled, keeping maxDate the bound.
        return this._nearestSelectableMonth(1) !== null
      }
      if (this.view === 'months') {
        // See canGoPrev: month-precision navigation steps over disabled years, so the
        // forward arrow stays live as long as a later selectable year is reachable.
        if (precision === 'month') return this._nearestSelectableYear(1) !== null
        return !this._isYearDisabled(this.year + 1)
      }
      if (this.view === 'years') {
        const blockStart = Math.floor(this.year / 12) * 12
        // Check if any year in the next 12-year block is enabled
        for (let y = blockStart + 12; y < blockStart + 24; y++) {
          if (!this._isYearDisabled(y)) return true
        }
        return false
      }
      return true
    },

    /**
     * Compute CSS class object for a year cell in the year picker view.
     */
    yearClasses(cell: YearCell): Record<string, boolean> {
      const selected = this.year === cell.year && this.view === 'months'
      return {
        'rc-year': true,
        'rc-year--current': cell.isCurrentYear,
        'rc-year--selected': selected,
        'rc-year--disabled': cell.isDisabled,
      }
    },

    /**
     * Compute CSS class object for a month cell in the month picker view.
     */
    monthClasses(cell: MonthCell): Record<string, boolean> {
      return {
        'rc-month': true,
        'rc-month--current': cell.isCurrentMonth,
        'rc-month--selected': this._monthCellSelected(cell),
        'rc-month--disabled': cell.isDisabled,
        'rc-month--focused': this.focusedMonth === cell.month,
      }
    },

    /**
     * Whether a month cell is the currently selected month. Drives both the
     * `rc-month--selected` style and the cell's `aria-selected` state, so the two
     * never drift apart.
     */
    _monthCellSelected(cell: MonthCell): boolean {
      // Register the selection counter so Alpine re-evaluates on commit.
      void this._selectionRev
      if (precision === 'month') {
        // Highlight the committed month regardless of view (drives restore from value).
        const sel = this._selection.toArray()[0] as CalendarDate | undefined
        return !!sel && sel.year === cell.year && sel.month === cell.month
      }
      return this.month === cell.month && this.view === 'days'
    },

    /**
     * Get a localized "Month Year" label for a specific month grid.
     * @param gridIndex - Index into the `grid` array (0 for first month, 1 for second).
     */
    monthYearLabel(gridIndex: number): string {
      const g = this.grid[gridIndex]
      if (!g) return ''
      const d = new CalendarDate(g.year, g.month, 1)
      return d.format({ month: 'long', year: 'numeric' }, locale)
    },

    /**
     * Compute CSS class object for a day cell.
     * Returns an object keyed by class name with boolean values, suitable for Alpine `:class`.
     */
    dayClasses(cell: {
      date: CalendarDate
      isCurrentMonth: boolean
      isToday: boolean
      isDisabled: boolean
    }): Record<string, boolean> {
      // Read _selectionRev and _metadataRev to register them as Alpine dependencies.
      void this._selectionRev
      void this._metadataRev
      const d = cell.date
      const selected = this.isSelected(d)
      const rangeStart = this.isRangeStart(d)
      const rangeEnd = this.isRangeEnd(d)
      const inRange = this.isInRange(d, this.hoverDate ?? undefined)
      const isOtherMonth = !cell.isCurrentMonth

      // Hover preview constraint feedback: mark dates that would form an invalid range
      let rangeInvalid = false
      if (
        mode === 'range' &&
        this.hoverDate !== null &&
        !cell.isDisabled &&
        !selected &&
        (this._selection as RangeSelection).isPartial()
      ) {
        rangeInvalid = !this.isDateSelectableForRange(d)
      }

      // Metadata-driven classes
      const meta = this._getCachedDateMeta(d)
      const hasLabel = !!meta?.label
      const isAvailable = meta?.availability === 'available'
      const isUnavailable = meta?.availability === 'unavailable'

      const classes: Record<string, boolean> = {
        'rc-day': true,
        'rc-day--today': cell.isToday,
        'rc-day--selected': selected,
        'rc-day--range-start': rangeStart,
        'rc-day--range-end': rangeEnd,
        'rc-day--in-range': inRange && !rangeStart && !rangeEnd,
        'rc-day--disabled': cell.isDisabled,
        'rc-day--other-month': isOtherMonth,
        'rc-day--hidden': isOtherMonth && this.monthCount > 1,
        'rc-day--focused': this.focusedDate !== null && this.focusedDate.isSame(d),
        'rc-day--range-invalid': rangeInvalid,
        'rc-day--available': isAvailable,
        'rc-day--unavailable': isUnavailable,
        'rc-day--has-label': hasLabel,
      }

      // Apply custom cssClass from metadata
      if (meta?.cssClass) {
        for (const cls of meta.cssClass.split(/\s+/)) {
          if (cls) classes[cls] = true
        }
      }

      return classes
    },

    /**
     * Get tooltip text for a day cell explaining why it is disabled.
     * Returns an empty string for enabled dates.
     */
    dayTitle(cell: { date: CalendarDate; isDisabled: boolean }): string {
      void this._metadataRev
      const meta = this._getCachedDateMeta(cell.date)
      const parts: string[] = []

      if (cell.isDisabled) {
        const reasons = this._getDisabledReasons(cell.date)
        if (reasons.length > 0) {
          parts.push(reasons.join('; '))
        } else if (meta?.availability === 'unavailable') {
          parts.push('Unavailable')
        }
      }

      if (meta?.label) {
        parts.push(meta.label)
      }

      return parts.join(' — ')
    },

    /**
     * Get metadata for a day cell. Returns `DateMeta | undefined`.
     * Reads `_metadataRev` for Alpine reactivity.
     */
    dayMeta(cell: { date: CalendarDate }): DateMeta | undefined {
      void this._metadataRev
      return this._getCachedDateMeta(cell.date)
    },

    /**
     * Get inline style string for a day cell.
     * Sets `--color-calendar-day-meta` when the metadata has a custom color.
     */
    dayStyle(cell: { date: CalendarDate }): string {
      void this._metadataRev
      const meta = this._getCachedDateMeta(cell.date)
      if (meta?.color) {
        return `--color-calendar-day-meta: ${meta.color};`
      }
      return ''
    },

    // --- Input binding ---

    /**
     * Bind an input element to the calendar.
     *
     * Sets up input masking (if enabled), syncs the initial value, and
     * attaches an input listener to keep `inputValue` in sync.
     *
     * Usage:
     * ```html
     * <!-- Alpine component root with calendar() -->
     *   <input x-ref="rc-input" type="text">
     * ```
     * The component auto-binds to the ref named by `config.inputRef` (default: `rc-input`) during init().
     * For custom refs, set `inputRef` in config or call `bindInput($refs.myInput)` explicitly.
     */
    bindInput(el: HTMLInputElement) {
      // Clean up previous binding
      if (this._detachInput) {
        this._detachInput()
        this._detachInput = null
      }

      this._inputEl = el

      // Set initial value
      el.value = this.inputValue

      // Month-precision pickers are selection-only: a 'MMMM YYYY' value can't be
      // re-parsed from typed text, so make the input read-only (click to open).
      // Capture the original state so detach restores it — the input may have been
      // read-only for the consumer's own reasons, which we must not clobber.
      const hadReadOnly = el.readOnly
      if (precision === 'month') {
        el.readOnly = true
      }

      // Attach mask if enabled (auto-disabled for month-name formats)
      if (useMask && !formatHasMonthName(format)) {
        this._detachInput = attachMask(el, format)
        // attachMask reformats the existing value through the mask
        this.inputValue = el.value
      }

      // Listen for input changes to keep inputValue in sync
      const syncHandler = () => {
        if (!this._syncing) {
          this.inputValue = el.value
        }
      }
      el.addEventListener('input', syncHandler)

      // Attach focus/blur handlers for popup mode
      const focusHandler = () => this.handleFocus()
      const blurHandler = () => this.handleBlur()
      el.addEventListener('focus', focusHandler)
      el.addEventListener('blur', blurHandler)

      // Set aria attributes for popup combobox pattern
      if (display === 'popup') {
        el.setAttribute('role', 'combobox')
        el.setAttribute('aria-haspopup', 'dialog')
        el.setAttribute('aria-expanded', String(this.isOpen))
        el.setAttribute('autocomplete', 'off')
        if (inputId && !el.id) {
          el.id = inputId
        }
        if (!el.getAttribute('aria-label')) {
          el.setAttribute('aria-label', this.inputAriaLabel)
        }
      }

      // Extend detach to include all listeners and aria attributes
      const prevDetach = this._detachInput
      this._detachInput = () => {
        prevDetach?.()
        if (precision === 'month') {
          el.readOnly = hadReadOnly
        }
        el.removeEventListener('input', syncHandler)
        el.removeEventListener('focus', focusHandler)
        el.removeEventListener('blur', blurHandler)
        if (display === 'popup') {
          el.removeAttribute('role')
          el.removeAttribute('aria-haspopup')
          el.removeAttribute('aria-expanded')
          el.removeAttribute('autocomplete')
        }
      }

      // Set placeholder if not already set
      if (!el.placeholder) {
        el.placeholder = format.toLowerCase()
      }
    },

    /**
     * Handle input event for unbound inputs (using `:value` + `@input`).
     * When using `bindInput()`, this is handled automatically.
     */
    handleInput(e: Event) {
      const el = e.target as HTMLInputElement
      this.inputValue = el.value
    },

    /**
     * Handle focus on the input element.
     * Opens the calendar popup in popup display mode.
     */
    handleFocus() {
      if (this._suppressFocusOpen) {
        this._suppressFocusOpen = false
        return
      }
      this.open()
    },

    /**
     * Handle blur on the input element.
     * Parses the typed value, updates selection if valid, and reformats the input.
     */
    handleBlur() {
      // Month-precision pickers are read-only/selection-only; never re-parse typed text,
      // just re-assert the canonical formatted value.
      if (precision === 'month') {
        this._syncInputFromSelection()
        return
      }

      const value = this._inputEl ? this._inputEl.value : this.inputValue

      // Empty input → clear selection
      if (!value.trim()) {
        if (this._selection.toArray().length > 0) {
          this._selection.clear()
          this._selectionRev++
          this._emitChange()
        }
        this._syncInputFromSelection()
        return
      }

      // Try to parse and update selection
      let changed = false

      if (mode === 'single') {
        const parsed = parseDate(value, format, locale) ?? CalendarDate.fromISO(value)
        if (parsed && !this._isEffectivelyDisabled(parsed)) {
          const current = this._selection.toArray()
          const isSame =
            current.length === 1 && (current[0] as CalendarDate).toKey() === parsed.toKey()
          if (!isSame) {
            this._selection.clear()
            this._selection.toggle(parsed)
            this.month = parsed.month
            this.year = parsed.year
            changed = true
          }
        }
      } else if (mode === 'range') {
        const range = parseDateRange(value, format, locale)
        if (range) {
          let [start, end] = range
          if (end.isBefore(start)) {
            const tmp = start
            start = end
            end = tmp
          }
          if (
            !this._isEffectivelyDisabled(start) &&
            !this._isEffectivelyDisabled(end) &&
            this._isRangeValid(start, end)
          ) {
            const current = this._selection.toArray()
            const isSame =
              current.length === 2 &&
              (current[0] as CalendarDate).toKey() === start.toKey() &&
              (current[1] as CalendarDate).toKey() === end.toKey()
            if (!isSame) {
              this._selection.clear()
              ;(this._selection as RangeSelection).setRange(start, end)
              this.month = start.month
              this.year = start.year
              changed = true
            }
          }
        }
      } else if (mode === 'multiple') {
        const dates = parseDateMultiple(value, format, locale)
        const valid = dates.filter((d) => !this._isEffectivelyDisabled(d))
        if (valid.length > 0) {
          // Compare parsed dates with current selection to avoid silent data loss
          const newKeys = new Set(valid.map((d) => d.toKey()))
          const currentKeys = new Set(this._selection.toArray().map((d) => d.toKey()))
          const setsEqual =
            newKeys.size === currentKeys.size && [...newKeys].every((k) => currentKeys.has(k))
          if (!setsEqual) {
            this._selection.clear()
            for (const d of valid) {
              ;(this._selection as MultipleSelection).add(d)
            }
            const first = valid[0] as CalendarDate
            this.month = first.month
            this.year = first.year
            changed = true
          }
        }
      }

      if (changed) {
        this._selectionRev++
        this._emitChange()
      }

      // Always reformat input to canonical display
      this._syncInputFromSelection()
    },

    // --- Internal: input sync ---

    /** Update inputValue and bound input element from current selection. */
    _syncInputFromSelection() {
      this._syncing = true
      this.inputValue = this.formattedValue

      if (this._inputEl) {
        this._inputEl.value = this.inputValue
      }

      this._syncing = false
    },

    /** Announce a message to screen readers via the aria-live status region. */
    _announce(message: string) {
      this._statusMessage = ''
      alpine(this).$nextTick(() => {
        this._statusMessage = message
      })
    },

    /** Announce the current navigation context (month/year/decade) for screen readers. */
    _announceNavigation() {
      if (this.view === 'days') {
        if (this.monthCount > 1 && !this.isScrollable) {
          // Dual-month: announce both months
          const labels: string[] = []
          for (let i = 0; i < this.grid.length; i++) {
            labels.push(this.monthYearLabel(i))
          }
          this._announce(labels.join(' \u2013 '))
        } else {
          this._announce(this.monthYearLabel(0))
        }
      } else if (this.view === 'months') {
        this._announce('Year: ' + this.year)
      } else if (this.view === 'years') {
        this._announce('Decade: ' + this.decadeLabel)
      }
    },

    /** Announce the new view after a view switch for screen readers. */
    _announceViewChange() {
      if (this.view === 'days') {
        this._announce(this.monthYearLabel(0))
      } else if (this.view === 'months') {
        this._announce('Select month, ' + this.year)
      } else if (this.view === 'years') {
        this._announce('Select year, ' + this.decadeLabel)
      }
    },

    /** Dispatch a CustomEvent on the component root element (not the evaluation context). */
    _dispatchOnRoot(event: string, detail?: Record<string, unknown>) {
      this._rootEl?.dispatchEvent(new CustomEvent(event, { detail: detail ?? null, bubbles: true }))
    },

    /**
     * Mirror `data-rc-theme` from the component root onto the teleported overlay.
     * No-op for inline display (overlay is null) — for popups, this is what keeps
     * consumer scoped CSS like `[data-rc-theme="dark"] .rc-day--selected { ... }`
     * working after the overlay is appended to document.body.
     */
    _syncPortalTheme() {
      if (!this._popupOverlayEl || !this._rootEl) return
      const theme = this._rootEl.getAttribute('data-rc-theme')
      if (theme !== null) {
        this._popupOverlayEl.setAttribute('data-rc-theme', theme)
      } else {
        this._popupOverlayEl.removeAttribute('data-rc-theme')
      }
    },

    /**
     * Dispatch calendar:select (always) and calendar:change (on committed state only).
     *
     * `calendar:select` fires on every selection update — including partial range
     * (first click of two). Use this for UIs that need to track each click.
     *
     * `calendar:change` fires only when the selection is in a committed state:
     * - single: always (every toggle is a commit)
     * - multiple: always (every add/remove is a commit)
     * - range: only when complete (2 dates) or cleared to 0 — NOT on the first
     *   click of a two-click range
     *
     * This makes `calendar:change` a "settled value" signal suitable for form
     * submission, analytics, and consumers that only care about final values.
     */
    _emitChange() {
      const detail = {
        value: this._selection.toValue(),
        dates: this._selection.toArray().map((d: CalendarDate) => d.toISO()),
        formatted: this.formattedValue,
      }
      this._dispatchOnRoot('calendar:select', detail)
      if (this._isCommittedSelection()) {
        this._dispatchOnRoot('calendar:change', detail)
      }
    },

    /**
     * Whether the current selection represents a "committed" (settled) state.
     * See `_emitChange()` for the definition per mode.
     */
    _isCommittedSelection(): boolean {
      if (mode === 'range') {
        return !(this._selection as RangeSelection).isPartial()
      }
      return true
    },

    /** Dispatch calendar:navigate event on month/year change. */
    _emitNavigate() {
      this._dispatchOnRoot('calendar:navigate', {
        year: this.year,
        month: this.month,
        view: this.view,
      })
    },

    /** Dispatch calendar:view-change event on view switch. */
    _emitViewChange() {
      this._dispatchOnRoot('calendar:view-change', {
        view: this.view,
        year: this.year,
        month: this.month,
      })
    },

    // --- Navigation ---

    prev() {
      // Month-precision: jump to the nearest earlier selectable year, skipping any
      // disabled run. No-op (and no slide) when none is reachable, so the arrow never
      // strands the user on an out-of-range year.
      if (this.view === 'months' && precision === 'month') {
        const target = this._nearestSelectableYear(-1)
        if (target === null) return
        this._navDirection = 'prev'
        this.year = target
        return
      }
      if (this.view === 'days') {
        if (this.isScrollable) return
        // Mirror the month-precision year jump above: hop to the nearest earlier
        // selectable month, skipping any run of fully-unavailable months so a
        // within-range gap can't wall off the reachable months behind it. No-op when
        // none is reachable (defensive — the arrow is already disabled via canGoPrev).
        const target = this._nearestSelectableMonth(-1)
        if (target === null) return
        this._navDirection = 'prev'
        this.month = target.month
        this.year = target.year
        return
      }
      this._navDirection = 'prev'
      if (this.view === 'months') {
        this.year--
      } else if (this.view === 'years') {
        this.year -= 12
      }
    },

    next() {
      if (this.view === 'months' && precision === 'month') {
        const target = this._nearestSelectableYear(1)
        if (target === null) return
        this._navDirection = 'next'
        this.year = target
        return
      }
      if (this.view === 'days') {
        if (this.isScrollable) return
        // See prev(): hop to the nearest later selectable month, skipping a gap of
        // fully-unavailable in-range months. No-op when none is reachable (defensive —
        // canGoNext already disables the arrow in that case).
        const target = this._nearestSelectableMonth(1)
        if (target === null) return
        this._navDirection = 'next'
        this.month = target.month
        this.year = target.year
        return
      }
      this._navDirection = 'next'
      if (this.view === 'months') {
        this.year++
      } else if (this.view === 'years') {
        this.year += 12
      }
    },

    /**
     * Nearest selectable year in a direction (+1 forward, -1 back), skipping disabled
     * years. Month-precision pickers expose no year grid, so the prev/next arrows are
     * the only way across years; an adjacent-only check would strand a selectable year
     * sitting behind a disabled one (e.g. `disabledYears: [2027]` makes 2028 unreachable
     * from 2026). Returns null when no selectable year is reachable within
     * MONTH_NAV_YEAR_SCAN steps (e.g. every year beyond maxDate is disabled).
     */
    _nearestSelectableYear(direction: 1 | -1): number | null {
      for (let i = 1; i <= MONTH_NAV_YEAR_SCAN; i++) {
        const y = this.year + direction * i
        if (!this._isYearDisabled(y)) return y
      }
      return null
    },

    /**
     * Nearest selectable month in a direction (+1 forward, -1 back) from the current
     * (year, month), skipping any run of disabled months. The days-view prev/next
     * arrows are the only way across months, so an adjacent-only check would strand a
     * selectable month sitting behind a gap of fully-unavailable months (e.g. a booking
     * calendar with departures Aug–Oct then nothing until next May). Out-of-range months
     * are shallow-disabled (hence deep-disabled), so [minDate, maxDate] still bounds the
     * scan and an out-of-range month is never returned. Returns null when no selectable
     * month is reachable within MONTH_NAV_MONTH_SCAN steps (e.g. every month past maxDate
     * is disabled).
     */
    _nearestSelectableMonth(direction: 1 | -1): { year: number; month: number } | null {
      const from = new CalendarDate(this.year, this.month, 1)
      for (let i = 1; i <= MONTH_NAV_MONTH_SCAN; i++) {
        const d = from.addMonths(direction * i)
        if (!this._isMonthDisabled(d.year, d.month)) return { year: d.year, month: d.month }
      }
      return null
    },

    goToToday() {
      this.month = this._today.month
      this.year = this._today.year
      // Precision month is a month-only picker — never expose the day grid.
      if (precision === 'month') {
        this.view = 'months'
        this._rebuildMonthGrid()
        return
      }
      this.view = 'days'
    },

    // --- Selection ---

    selectDate(dateOrISO: CalendarDate | string) {
      const date = typeof dateOrISO === 'string' ? CalendarDate.fromISO(dateOrISO) : dateOrISO
      if (!date) return
      if (this._isEffectivelyDisabled(date)) return

      // Range validation: when about to complete a range, check min/max range
      if (mode === 'range') {
        const range = this._selection as RangeSelection
        const rangeStart = range.getStart()
        if (range.isPartial() && rangeStart && !date.isSame(rangeStart)) {
          // Determine actual start/end after potential swap
          let start: CalendarDate = rangeStart
          let end: CalendarDate = date
          if (end.isBefore(start)) {
            const tmp = start
            start = end
            end = tmp
          }
          if (!this._isRangeValid(start, end)) return
        }
      }

      // allowDeselect: false — re-clicking a selected date confirms it instead of
      // toggling it off. Skips beforeSelect too (there is no 'deselect' action to
      // veto), but still auto-closes the popup like a completed selection.
      // Range mode is untouched: its toggle() re-click semantics are part of the
      // start/end picking flow, not a deselection gesture.
      if (
        !allowDeselect &&
        (mode === 'single' || mode === 'multiple') &&
        this._selection.isSelected(date)
      ) {
        if (closeOnSelect && display === 'popup' && this.isOpen) this.close()
        return
      }

      // beforeSelect callback: allow consumers to prevent selection
      if (beforeSelectCb) {
        const action = this._selection.isSelected(date) ? 'deselect' : 'select'
        const result = beforeSelectCb(date, {
          mode,
          selectedDates: this._selection.toArray(),
          action,
        })
        if (result === false) return
      }

      // Capture pre-toggle state for announcements
      const wasSelected = this._selection.isSelected(date)
      const wasPartialRange = mode === 'range' && (this._selection as RangeSelection).isPartial()

      this._selection.toggle(date)
      // Sync all three wizard fields from the actual selection. A user can reach
      // selectDate() in wizard mode with a date whose month/year differs from the
      // earlier month/year step — e.g., when months >= 2 (including mobileMonths)
      // renders the adjacent month in the day grid — so relying on _wizardMonth /
      // _wizardYear set by selectMonth()/selectYear() alone leaves the wizard
      // summary disagreeing with the real selection.
      if (wizard) {
        this._wizardDay = date.day
        this._wizardMonth = date.month
        this._wizardYear = date.year
      }
      // Bump reactive counter so Alpine re-evaluates dayClasses() bindings.
      // No grid rebuild needed — grid structure doesn't change on selection.
      this._selectionRev++
      this._emitChange()
      this._syncInputFromSelection()

      // Announce selection to screen readers
      const dateLabel = date.format(
        { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
        locale,
      )
      if (mode === 'single') {
        this._announce(dateLabel + ' selected')
      } else if (mode === 'multiple') {
        const count = this._selection.toArray().length
        if (wasSelected) {
          this._announce(dateLabel + ' deselected. ' + count + ' dates selected')
        } else {
          this._announce(dateLabel + ' selected. ' + count + ' dates selected')
        }
      } else if (mode === 'range') {
        const range = this._selection as RangeSelection
        if (range.isPartial()) {
          this._announce('Range start: ' + dateLabel + '. Select end date')
        } else if (wasPartialRange) {
          const dates = range.toArray()
          if (dates.length === 2) {
            const startLabel = (dates[0] as CalendarDate).format(
              { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
              locale,
            )
            const endLabel = (dates[1] as CalendarDate).format(
              { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
              locale,
            )
            this._announce('Range: ' + startLabel + ' to ' + endLabel)
          }
        }
      }

      // Auto-close popup after selection is complete
      if (closeOnSelect && display === 'popup' && this.isOpen) {
        const isComplete =
          mode === 'single' ||
          (mode === 'range' && !(this._selection as RangeSelection).isPartial()) ||
          mode === 'multiple'
        if (isComplete) this.close()
      }
    },

    isSelected(date: CalendarDate): boolean {
      return this._selection.isSelected(date)
    },

    isInRange(date: CalendarDate, hoverDate?: CalendarDate): boolean {
      if (mode !== 'range') return false
      return (this._selection as RangeSelection).isInRange(date, hoverDate)
    },

    isRangeStart(date: CalendarDate): boolean {
      if (mode !== 'range') return false
      const start = (this._selection as RangeSelection).getStart()
      return start !== null && date.isSame(start)
    },

    isRangeEnd(date: CalendarDate): boolean {
      if (mode !== 'range') return false
      const end = (this._selection as RangeSelection).getEnd()
      return end !== null && date.isSame(end)
    },

    /**
     * Check whether selecting `date` as a range endpoint would produce a valid range.
     *
     * Returns `true` when:
     * - The date is not disabled AND
     * - Either no range start is selected yet (any non-disabled date can start), OR
     * - Completing the range with this date would satisfy minRange/maxRange constraints.
     *
     * Returns `false` for non-range modes.
     *
     * Useful for dimming dates in the UI that would result in an invalid range.
     */
    isDateSelectableForRange(date: CalendarDate): boolean {
      if (mode !== 'range') return false
      if (this._isEffectivelyDisabled(date)) return false

      const range = this._selection as RangeSelection

      // No start selected yet, or range is already complete (next click restarts)
      if (!range.isPartial()) return true

      const rangeStart = range.getStart()
      if (!rangeStart) return true

      // Clicking the same date as start deselects — always allowed
      if (date.isSame(rangeStart)) return true

      // Determine actual start/end after potential swap
      let start = rangeStart
      let end = date
      if (end.isBefore(start)) {
        const tmp = start
        start = end
        end = tmp
      }

      return this._isRangeValid(start, end)
    },

    /**
     * Get human-readable reasons why a date is disabled.
     * Returns an empty array for enabled dates.
     * Accepts a CalendarDate or ISO string.
     */
    getDisabledReason(date: CalendarDate | string): string[] {
      const d = typeof date === 'string' ? CalendarDate.fromISO(date) : date
      if (!d) return []
      return this._getDisabledReasons(d)
    },

    clearSelection() {
      this._selection.clear()
      this._selectionRev++
      this._emitChange()
      this._syncInputFromSelection()
      this._announce('Selection cleared')
    },

    // --- Programmatic control ---

    /**
     * Set the calendar selection programmatically.
     *
     * Accepts a single ISO date string, an array of ISO strings, or CalendarDate(s).
     * The calendar navigates to show the first selected date.
     *
     * Usage:
     * ```js
     * // Single mode
     * $data.setValue('2025-06-15')
     *
     * // Multiple mode
     * $data.setValue(['2025-06-15', '2025-06-20'])
     *
     * // Range mode
     * $data.setValue(['2025-06-15', '2025-06-20'])
     *
     * // With CalendarDate
     * $data.setValue(new CalendarDate(2025, 6, 15))
     * ```
     */
    setValue(value: string | string[] | CalendarDate | CalendarDate[]) {
      this._selection.clear()

      // Precision month: the month is the unit of selection. Accept the value when
      // its month is selectable and normalize to the first of the month — matching
      // what _commitMonth() emits and what the initial-value path stores. The
      // day-level _isEffectivelyDisabled check would wrongly reject a first-of-month
      // value that precedes a mid-month minDate (e.g. 2026-06-01 with minDate
      // 2026-06-15), even though the picker offers that month.
      if (precision === 'month') {
        const raw = Array.isArray(value) ? value[0] : value
        const d =
          raw instanceof CalendarDate
            ? raw
            : typeof raw === 'string'
              ? (CalendarDate.fromISO(raw) ?? parseDate(raw, format, locale))
              : null
        if (d && !this._isMonthDisabled(d.year, d.month)) {
          this.year = d.year
          this.month = d.month
          this._selection.toggle(new CalendarDate(d.year, d.month, 1))
        }
        this._selectionRev++
        this._emitChange()
        this._syncInputFromSelection()
        return
      }

      const dates: CalendarDate[] = []

      if (typeof value === 'string') {
        // Single ISO string
        const d = CalendarDate.fromISO(value) ?? parseDate(value, format, locale)
        if (d && !this._isEffectivelyDisabled(d)) {
          dates.push(d)
        }
      } else if (Array.isArray(value)) {
        for (const v of value) {
          const d =
            typeof v === 'string' ? (CalendarDate.fromISO(v) ?? parseDate(v, format, locale)) : v
          if (d && !this._isEffectivelyDisabled(d)) {
            dates.push(d)
          }
        }
      } else if (value instanceof CalendarDate) {
        if (!this._isEffectivelyDisabled(value)) {
          dates.push(value)
        }
      }

      // Range validation
      if (mode === 'range' && dates.length === 2) {
        let [start, end] = dates as [CalendarDate, CalendarDate]
        if (end.isBefore(start)) {
          const tmp = start
          start = end
          end = tmp
        }
        if (!this._isRangeValid(start, end)) {
          // Invalid range — don't set anything
          this._selectionRev++
          this._emitChange()
          this._syncInputFromSelection()
          return
        }
        // Use setRange() for direct assignment — avoids toggle() deselecting same-day ranges
        ;(this._selection as RangeSelection).setRange(start, end)
      } else if (mode === 'multiple') {
        for (const d of dates) {
          ;(this._selection as MultipleSelection).add(d)
        }
      } else {
        for (const d of dates) {
          this._selection.toggle(d)
        }
      }

      // Navigate to the first selected date
      const selected = this._selection.toArray()
      if (selected.length > 0) {
        const first = selected[0] as CalendarDate
        this.month = first.month
        this.year = first.year
      }

      this._selectionRev++
      this._emitChange()
      this._syncInputFromSelection()
    },

    /**
     * Clear the current selection. Alias for `clearSelection()`.
     */
    clear() {
      this.clearSelection()
    },

    /**
     * Navigate the calendar to a specific year and month without changing selection.
     *
     * Usage:
     * ```js
     * $data.goTo(2025, 6)   // Navigate to June 2025
     * $data.goTo(2030)      // Navigate to the current month in 2030
     * ```
     */
    goTo(year: number, month?: number) {
      this.year = year
      if (month !== undefined) {
        this.month = month
      }
      // Precision month is a month-only picker — keep callers on the months grid
      // and refresh it for the new year instead of exposing the day grid.
      if (precision === 'month') {
        this.view = 'months'
        this._rebuildMonthGrid()
        return
      }
      this.view = 'days'
      if (this.isScrollable) {
        this._rebuildGrid()
        alpine(this).$nextTick(() => {
          this._rebindScrollObserver()
          this._scrollToMonth(year, month ?? this.month)
        })
      }
    },

    /**
     * Get the current selection as an array of CalendarDate objects.
     *
     * Returns a new array each time (safe to mutate).
     *
     * Usage:
     * ```js
     * const dates = $data.getSelection()
     * console.log(dates.map(d => d.toISO()))
     * ```
     */
    getSelection(): CalendarDate[] {
      return [...this._selection.toArray()]
    },

    /**
     * Apply a predefined range preset by index.
     *
     * Evaluates the preset's `value()` function, sets the selection to the
     * returned `[start, end]` range, navigates to the start date, and
     * emits a change event. In popup mode with `closeOnSelect`, the popup closes.
     *
     * Usage in Alpine template:
     * ```html
     * <template x-for="(preset, pi) in presets" :key="pi">
     *   <button @click="applyPreset(pi)" x-text="preset.label"></button>
     * </template>
     * ```
     */
    applyPreset(index: number) {
      const preset = this.presets[index]
      if (!preset) return

      const [start, end] = preset.value()

      // In single mode, only use the start date — presets return [start, end] pairs
      // but single mode should not receive an array (avoids toggle-on/toggle-off bug)
      if (mode === 'single') {
        this.setValue(start)
      } else {
        this.setValue([start, end])
      }

      // Close popup if configured
      if (this.display === 'popup' && closeOnSelect) {
        this.close()
      }
    },

    // --- Runtime config ---

    /**
     * Update constraint-related configuration at runtime.
     *
     * Rebuilds all constraint functions and refreshes grids. Accepts the same
     * constraint properties as `CalendarConfig` (minDate, maxDate, disabledDates,
     * disabledDaysOfWeek, enabledDates, enabledDaysOfWeek, disabledMonths,
     * enabledMonths, disabledYears, enabledYears, minRange, maxRange, rules).
     *
     * Updates are **merged** with the existing constraint config. To remove a
     * previously set constraint, pass `undefined` explicitly (e.g.
     * `{ disabledDaysOfWeek: undefined }`).
     *
     * Usage:
     * ```js
     * $data.updateConstraints({ minDate: '2025-06-01', disabledDaysOfWeek: [0, 6] })
     * ```
     */
    updateConstraints(updates: Partial<CalendarConfig>) {
      // Apply the same Blade/Twig JSON-string tolerance the init path has, so
      // reactive bindings that feed HTML-attribute-stringified arrays in here
      // don't silently degrade into character-iterated Sets.
      coerceConfigArrays(updates as CalendarConfig)
      // Merge: explicit undefined removes a key, otherwise overlay on existing
      const merged = { ...this._constraintConfig }
      for (const key of CONSTRAINT_KEYS) {
        if (key in updates) {
          ;(merged as Record<string, unknown>)[key] = (updates as Record<string, unknown>)[key]
        }
      }
      this._constraintConfig = merged
      const c = buildConstraints(merged, constraintMessages)
      this._isDisabledDate = c.isDisabledDate
      this._isRangeValid = c.isRangeValid
      this._getDisabledReasons = c.getDisabledReasons
      this._wrapWithDeepChecks(c)
      this._rebuildGrid()
      this._rebuildMonthGrid()
      this._rebuildYearGrid()
      if (this.isScrollable) {
        alpine(this).$nextTick(() => {
          this._rebindScrollObserver()
        })
      }
    },

    /**
     * Update the date metadata provider at runtime.
     *
     * Normalizes the provider, bumps the reactive counter, and rebuilds
     * the day grid (since unavailability affects disabled state).
     *
     * Usage:
     * ```js
     * $data.updateDateMetadata({
     *   '2026-03-15': { label: '$150', availability: 'available' },
     * })
     * ```
     */
    updateDateMetadata(provider: DateMetaProvider | undefined | null) {
      this._getDateMeta = normalizeDateMeta(provider)
      this._metadataRev++
      this._rebuildGrid()
      this._rebuildMonthGrid()
      this._rebuildYearGrid()
      if (this.isScrollable) {
        alpine(this).$nextTick(() => {
          this._rebindScrollObserver()
        })
      }
    },

    // --- View switching ---

    setView(newView: 'days' | 'months' | 'years') {
      // Month precision is a single month-grid step: the day grid and year step are
      // never part of its flow, so refuse to switch away from the months view.
      if (precision === 'month' && newView !== 'months') return
      this.view = newView
    },

    selectMonth(targetMonth: number) {
      if (this._isMonthDisabled(this.year, targetMonth)) return

      // Precision month: the month is the unit of selection — commit it and finish.
      if (precision === 'month') {
        this._commitMonth(this.year, targetMonth)
        return
      }

      this.month = targetMonth
      this._wizardMonth = targetMonth

      if (wizardMode === 'year-month') {
        // Find first selectable day in month (day 1 may be disabled)
        const selectableDay = this._firstSelectableDay(this.year, targetMonth)
        if (!selectableDay) return
        this.wizardStep = wizardTotalSteps
        this.selectDate(selectableDay)
        return
      }

      this.view = 'days'
      if (wizard) {
        this.wizardStep = wizardMode === 'month-day' ? 2 : 3
      }
    },

    /**
     * Commit the first day of the given month as the single selection.
     *
     * Used by `precision: 'month'`. The month is the unit of selection, so the
     * month-level disabled check (not the day-level one) is authoritative: the
     * representative day — the 1st — is committed even when it precedes `minDate`
     * (e.g. minDate is mid-month). Emits `calendar:change` with `dates[0]` =
     * first-of-month, syncs the formatted input, and closes the popup.
     */
    _commitMonth(year: number, month: number) {
      if (this._isMonthDisabled(year, month)) return
      const first = new CalendarDate(year, month, 1)

      // beforeSelect hook — a month commit is always a 'select' action.
      if (beforeSelectCb) {
        const result = beforeSelectCb(first, {
          mode,
          selectedDates: this._selection.toArray(),
          action: 'select',
        })
        if (result === false) return
      }

      this.year = year
      this.month = month
      this._selection.clear()
      this._selection.toggle(first)
      this._selectionRev++
      this._emitChange()
      this._syncInputFromSelection()

      this._announce(first.format({ month: 'long', year: 'numeric' }, locale) + ' selected')

      // Selecting a month is terminal — close the popup like a completed single selection.
      if (closeOnSelect && display === 'popup' && this.isOpen) {
        this.close()
      }
    },

    selectYear(targetYear: number) {
      if (this._isYearDisabled(targetYear)) return
      this.year = targetYear
      this._wizardYear = targetYear
      this.view = 'months'
      if (this.wizard) this.wizardStep = 2
    },

    /** Navigate the wizard back one step. No-op if not in wizard mode. */
    wizardBack() {
      if (!this.wizard) return
      if (wizardMode === 'full') {
        if (this.wizardStep === 3) {
          this.wizardStep = 2
          this.view = 'months'
          this._wizardMonth = null
          this._wizardDay = null
        } else if (this.wizardStep === 2) {
          this.wizardStep = 1
          this.view = 'years'
          this._wizardYear = null
          this._wizardMonth = null
        }
      } else if (wizardMode === 'year-month') {
        if (this.wizardStep === 2) {
          this.wizardStep = 1
          this.view = 'years'
          this._wizardYear = null
          this._wizardMonth = null
        }
      } else if (wizardMode === 'month-day') {
        if (this.wizardStep === 2) {
          this.wizardStep = 1
          this.view = 'months'
          this._wizardMonth = null
        }
      }
    },

    // --- Popup ---

    open() {
      if (this.display !== 'popup') return

      // Reset wizard to first step on reopen
      if (wizard) {
        this.wizardStep = 1
        this.view = wizardStartView
        this._wizardYear = null
        this._wizardMonth = null
        this._wizardDay = null
        if (wizardMode !== 'month-day') {
          this.year = this._today.year - 30
          this._rebuildYearGrid()
        }
      } else if (precision === 'month') {
        // Always reopen on the months grid, repositioned onto the committed selection.
        this.view = 'months'
        const sel = this._selection.toArray()[0] as CalendarDate | undefined
        if (sel) {
          this.year = sel.year
          this.month = sel.month
        }
        this._rebuildMonthGrid()
      }

      this.isOpen = true
      this._inputEl?.setAttribute('aria-expanded', 'true')
      // Pick up any data-rc-theme change the consumer may have made between opens.
      this._syncPortalTheme()
      this._dispatchOnRoot('calendar:open')

      // CSS handles centered modal layout via `.rc-popup-overlay`
      alpine(this).$nextTick(() => {
        // Focus management after popup renders
      })
    },

    close() {
      if (this.display !== 'popup') return
      this.isOpen = false
      this._inputEl?.setAttribute('aria-expanded', 'false')
      this._dispatchOnRoot('calendar:close')
    },

    toggle() {
      if (this.isOpen) {
        this.close()
      } else {
        this.open()
      }
    },

    /**
     * Handle keydown events on the calendar container.
     *
     * Supports full keyboard navigation per ARIA grid pattern:
     * - Arrow keys: move focus between days (±1 day / ±7 days)
     * - Enter/Space: select the focused day
     * - PageUp/PageDown: navigate prev/next month (+ Shift: prev/next year)
     * - Home/End: jump to first/last day of current month
     * - Escape: return to day view from month/year picker, or close popup
     * - Tab: natural tab order (exits calendar)
     *
     * Uses `aria-activedescendant` pattern: the calendar grid container holds
     * focus (`tabindex="0"`), and `focusedDate` drives which cell is visually
     * highlighted. Day cells should have `id="day-{ISO}"` in the template.
     *
     * Usage in Alpine template:
     * ```html
     * <div @keydown="handleKeydown($event)" tabindex="0"
     *      :aria-activedescendant="focusedDateISO ? 'day-' + focusedDateISO : null">
     * ```
     */
    handleKeydown(e: KeyboardEvent) {
      // --- Escape: wizard back → view fallback → close popup → do nothing ---
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        // Wizard mode: go back one step instead of jumping to days
        if (this.wizard && this.wizardStep > 1) {
          this.wizardBack()
          return
        }
        // If in month or year picker, return to days view. Month precision has no
        // day view to fall back to — the months grid is the whole picker — so let
        // Escape fall through to the popup-close behavior instead of exposing days.
        if (precision !== 'month' && (this.view === 'months' || this.view === 'years')) {
          this.view = 'days'
          return
        }
        // If popup is open, close it and return focus to input
        if (display === 'popup' && this.isOpen) {
          this.close()
          if (this._inputEl) {
            this._suppressFocusOpen = true
            this._inputEl.focus()
          }
        }
        return
      }

      // --- Day view keyboard navigation ---
      if (this.view === 'days') {
        switch (e.key) {
          // Navigation keys: auto-initialize focusedDate if not set
          case 'ArrowRight':
          case 'ArrowLeft':
          case 'ArrowDown':
          case 'ArrowUp':
          case 'PageDown':
          case 'PageUp':
          case 'Home':
          case 'End': {
            e.preventDefault()
            // Auto-init focusedDate on first navigation keypress
            if (!this.focusedDate) {
              const selected = this._selection.toArray()
              if (selected.length > 0) {
                this.focusedDate = selected[0] as CalendarDate
              } else {
                this.focusedDate = new CalendarDate(this.year, this.month, 1)
              }
            }
            // Now dispatch to the specific navigation action
            if (e.key === 'ArrowRight') this._moveFocus(1)
            else if (e.key === 'ArrowLeft') this._moveFocus(-1)
            else if (e.key === 'ArrowDown') this._moveFocus(7)
            else if (e.key === 'ArrowUp') this._moveFocus(-7)
            else if (e.key === 'PageDown') this._moveFocusByMonths(e.shiftKey ? 12 : 1)
            else if (e.key === 'PageUp') this._moveFocusByMonths(e.shiftKey ? -12 : -1)
            else if (e.key === 'Home')
              this._setFocusedDate(new CalendarDate(this.year, this.month, 1))
            else if (e.key === 'End')
              this._setFocusedDate(new CalendarDate(this.year, this.month, 1).endOfMonth())
            return
          }
          // Selection keys: only act when focus is already established
          case 'Enter':
          case ' ':
            if (this.focusedDate) {
              e.preventDefault()
              this.selectDate(this.focusedDate)
            }
            return
        }
      }

      // --- Month view keyboard navigation ---
      // The months grid is the whole picker for precision month (and the wizard's
      // month step), so it needs the same roving-focus model the day grid has.
      if (this.view === 'months' && precision === 'month') {
        const cols = this.monthGrid[0]?.length || 3
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowLeft':
          case 'ArrowDown':
          case 'ArrowUp':
          case 'Home':
          case 'End': {
            e.preventDefault()
            const current = this.focusedMonth ?? this.month
            let next = current
            if (e.key === 'ArrowRight') next = current + 1
            else if (e.key === 'ArrowLeft') next = current - 1
            else if (e.key === 'ArrowDown') next = current + cols
            else if (e.key === 'ArrowUp') next = current - cols
            else if (e.key === 'Home') next = 1
            else if (e.key === 'End') next = 12
            this.focusedMonth = Math.min(12, Math.max(1, next))
            return
          }
          // Page keys move between years, mirroring the day grid's month paging.
          // Gate on canGoNext/canGoPrev so the keyboard path honours the same hard
          // min/max limits that disable the header arrows — otherwise PageDown/PageUp
          // would page onto an out-of-range year that the buttons forbid.
          case 'PageDown':
          case 'PageUp':
            e.preventDefault()
            if (e.key === 'PageDown') {
              if (this.canGoNext) this.next()
            } else if (this.canGoPrev) {
              this.prev()
            }
            return
          case 'Enter':
          case ' ':
            if (this.focusedMonth !== null) {
              e.preventDefault()
              this.selectMonth(this.focusedMonth)
            }
            return
        }
      }
    },

    // --- Internal: keyboard focus management ---

    /**
     * Move focusedDate by a number of days, navigating months as needed.
     * Skips disabled dates in the direction of movement (up to 31 attempts).
     */
    _moveFocus(deltaDays: number) {
      if (!this.focusedDate) return
      let candidate = this.focusedDate.addDays(deltaDays)

      // Skip disabled dates (up to 31 attempts to avoid infinite loops)
      let attempts = 0
      while (this._isEffectivelyDisabled(candidate) && attempts < 31) {
        candidate = candidate.addDays(deltaDays > 0 ? 1 : -1)
        attempts++
      }

      // If all candidates were disabled, don't move
      if (this._isEffectivelyDisabled(candidate)) return

      this._setFocusedDate(candidate)
    },

    /**
     * Move focusedDate by a number of months, clamping the day to valid range.
     * If the target month is fully unavailable, hops across the gap to the nearest
     * selectable month — keeping PageUp/PageDown consistent with the header arrows.
     */
    _moveFocusByMonths(deltaMonths: number) {
      if (!this.focusedDate) return
      const step = deltaMonths > 0 ? 1 : -1
      const target = this.focusedDate.addMonths(deltaMonths)

      // Skip disabled dates within/just past the target month (up to 31 attempts),
      // matching _moveFocus() behavior.
      let candidate = target
      let attempts = 0
      while (this._isEffectivelyDisabled(candidate) && attempts < 31) {
        candidate = candidate.addDays(step)
        attempts++
      }
      if (!this._isEffectivelyDisabled(candidate)) {
        this._setFocusedDate(candidate)
        return
      }

      // The target month is fully unavailable and the day-by-day skip couldn't escape it
      // (a multi-month gap). Mirror the header arrows (canGoPrev/canGoNext → next()/prev()
      // → _nearestSelectableMonth): hop across the run of unavailable months to the nearest
      // selectable month in this direction and focus its first selectable day, so keyboard
      // paging crosses the same gaps the arrows do instead of dead-ending at the gap.
      // Out-of-range months are deep-disabled, so minDate/maxDate still bound the hop.
      const base = new CalendarDate(target.year, target.month, 1)
      for (let i = 0; i <= MONTH_NAV_MONTH_SCAN; i++) {
        const m = base.addMonths(step * i)
        if (this._isMonthDisabled(m.year, m.month)) continue
        const day = this._firstSelectableDay(m.year, m.month)
        if (day) {
          this._setFocusedDate(day)
          return
        }
      }
    },

    /**
     * Set focusedDate and navigate the calendar view if needed.
     */
    _setFocusedDate(date: CalendarDate) {
      this.focusedDate = date
      if (this.isScrollable) {
        // Scroll the focused date's month into view
        alpine(this).$nextTick(() => {
          this._scrollToMonth(date.year, date.month)
        })
        return
      }
      // Navigate the calendar view to show the focused date's month
      if (date.month !== this.month || date.year !== this.year) {
        this.month = date.month
        this.year = date.year
      }
    },

    // --- Internal: scrollable multi-month ---

    /** Label for the sticky header in scrollable mode — tracks the topmost visible month. */
    get scrollHeaderLabel(): string {
      if (!this.isScrollable) return ''
      const mg = this.grid[this._scrollVisibleIndex]
      if (!mg) return ''
      const d = new CalendarDate(mg.year, mg.month, 1)
      return d.format({ month: 'long', year: 'numeric' }, locale)
    },

    /**
     * Attach an IntersectionObserver that updates the sticky header as the user scrolls.
     *
     * Rendering is driven by Alpine's x-if (scrollable wrapper) → x-for (month nodes)
     * chain, and the number of reactive ticks between a state change and the final DOM
     * varies by entry path (init, view switch, breakpoint flip). Rather than hard-code
     * tick counts at every call site, retry internally: if the container or its month
     * children aren't present yet, reschedule on the next tick. Bounded at 3 retries
     * so a genuinely-missing container (e.g., non-scrollable view) exits cleanly.
     *
     * Retries are tagged with a token captured on the initial entry. If another bind
     * starts while a retry is queued, `_scrollInitToken` advances and the stale retry
     * returns without running — preventing two observers being attached to the same
     * container when a later call succeeds before an older queued retry fires.
     */
    _initScrollListener(retriesLeft = 3, token?: number) {
      if (typeof IntersectionObserver === 'undefined') return
      // First entry (no token passed) starts a new session. Disconnect any existing
      // observer before allocating a fresh one — if a caller reaches this function
      // directly (init path) after another caller already bound an observer (e.g., a
      // breakpoint handler that fired synchronously during startup), the prior
      // observer would otherwise be overwritten in _scrollObserver and leak with no
      // handle to disconnect it. Retries carry a token and skip this step.
      const isFreshEntry = token === undefined
      if (isFreshEntry && this._scrollObserver) {
        this._scrollObserver.disconnect()
        this._scrollObserver = null
      }
      const myToken = token ?? ++this._scrollInitToken
      // Stale retry — a newer bind session has started, abandon this one.
      if (myToken !== this._scrollInitToken) return

      const searchRoot = this._popupOverlayEl ?? alpine(this).$el
      const container = searchRoot.querySelector('.rc-months--scroll') as HTMLElement | null
      if (!container) {
        if (retriesLeft > 0 && this.isScrollable && this.view === 'days') {
          alpine(this).$nextTick(() => this._initScrollListener(retriesLeft - 1, myToken))
        }
        return
      }
      this._scrollContainerEl = container

      const monthEls = container.querySelectorAll('[data-month-id]')
      if (monthEls.length === 0) {
        if (retriesLeft > 0) {
          alpine(this).$nextTick(() => this._initScrollListener(retriesLeft - 1, myToken))
        }
        return
      }

      const indexMap = new Map<Element, number>()
      monthEls.forEach((el, i) => indexMap.set(el, i))

      const visible = new Set<number>()

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const idx = indexMap.get(entry.target)
            if (idx === undefined) continue
            if (entry.isIntersecting) {
              visible.add(idx)
            } else {
              visible.delete(idx)
            }
          }
          if (visible.size > 0) {
            const minIdx = Math.min(...visible)
            if (this._scrollVisibleIndex !== minIdx) {
              this._scrollVisibleIndex = minIdx
            }
          }
        },
        { root: container, rootMargin: '0px 0px -90% 0px', threshold: 0 },
      )

      monthEls.forEach((el) => observer.observe(el))
      this._scrollObserver = observer
    },

    /** Disconnect and re-initialize the scroll observer after DOM rebuild. */
    _rebindScrollObserver() {
      if (this._scrollObserver) {
        this._scrollObserver.disconnect()
        this._scrollObserver = null
      }
      this._initScrollListener()
    },

    /** Smooth scroll a specific month into view inside the scroll container. */
    _scrollToMonth(year: number, month: number) {
      if (!this._scrollContainerEl) return
      const el = this._scrollContainerEl.querySelector(
        `[data-month-id="month-${year}-${month}"]`,
      ) as HTMLElement | null
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
  }
}
